import $ from '@sparta.global.vendors/jquery';
import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import spartaStoreUtility from '@sparta.global.utilities/sparta-store-utility/dist/es6.js';
import '@sparta.global.utilities/sparta-transport-utility/dist/es6.js';
import DataAssetLoader from '@sparta.global.utilities/data-asset-loader-utility/dist/es6.js';
import spartaGlobalFunctions from '@sparta.global.utilities/sparta-global-functions-utility/dist/es6.js';

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * @name        AML Validation Rules
 * @file        aml-validation-rules.js
 * @description Provides AML approved validation logic for inputs
 * @classdesc   Provides AML approved validation logic for inputs
 * @global      Creates a a global AMLValidationRules object
 * @author      James A. Black Jr.
 * @copyright   © 2017 Bank of America Corporation. All rights reserved.
 *
 * The AML Validation Rules is used to extend the Sparta Validation Framework, in order to support
 * a standard AML approved form validation experience across the Bank of America websites.  The
 * pre-defined validation rules each contain granular logic, intended to check for a multiple
 * validations.  By combining multiple rules together for an individual field, along with the
 * support for valid/invalid lists from external JSON files, each field will be validated against
 * a set of critera.  When a field fails a single validation rule, the error message displayed will
 * be the appropriate error message for that rule.
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

/**
 * @const STRINGS
 * @description constant for storing common strings used in this utility
 */
const STRINGS = {
  UNITED_STATES: 'US',
  CANADA: 'CANADA',
  INTERNATIONAL: 'INTERNATIONAL',
  ENGLISH_LANGUAGE: 'en',
  SPANISH_LANGUAGE: 'es',
  GWIM: 'GWIM',
  ISO2: 'ISO-2',
  ISO3166: 'ISO-3166',
  CONTAINS: 'Contains',
  EXACT_MATCH: 'Exact Match',
  CITY_COUNTY_EN: 'Start with a letter and follow with only letters, spaces, periods (.) and hyphens (-).',
  CITY_COUNTY_ES: 'Comience con una letra y siga solo con letras, espacios, puntos (.) y guiones (-).'
};
class AMLValidationRules {
  init(validator, validationRulesImporter) {
    const self = this;
    this.language = spartaStoreUtility.getData('lang').split('-')[0];
    this.importer = validationRulesImporter;
    this.validator = validator;

    /**
     * @const {Object} AML
     * @description a constant containing AML validation functions
     */
    const AML = {
      /* "validate.AML.firstName" method
       * "AML.firstName" validates that an input value contains only characters that are valid
       * for first name data.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      maxLength (integer) [0]:  Optionally contain an integer, indicating
       *                          the maximum length. The integer must be greater than 0.
       *                          [Default: 40]
       *
       *                      allowSpecialChars (boolean) [1]:  A boolean value representing wether
       *                          or not to allow special characters. [Default: true]
       *
       * Returns:
       *    true  - if the input value matches the first name format
       *    false - if the input value does not match the first name format
       */
      firstName($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.firstName',
          keyName: 'name',
          // minLength: 2,
          maxLength: 40,
          maxRepeatedChar: ["a-zA-Z0-9 '.-", 2],
          maxRepeatExceptions: ['VIII', 'VIII.'],
          allowSpecialChars: true
        };
        let options = {
          maxLength: defaults.maxLength,
          allowSpecialChars: defaults.allowSpecialChars
        };
        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);
        const validations = {
          // minLength: [options.minLength],
          nameCharacters: [options.allowSpecialChars],
          maxLength: [options.maxLength]
        };
        let validRepeatException = false;
        options.maxRepeatExceptions.every(value => {
          const validException = $element.val().toUpperCase() === value;
          if (validException) {
            validRepeatException = true;
            return false;
          }
          return true;
        });
        if (!validRepeatException) {
          validations.maxRepeatedChar = options.maxRepeatedChar;
        }
        const validationsResult = self.getValidationResults($element, validations);
        const invalidListResult = self.getInvalidResults($element, options.ruleName, options.keyName);
        if (validationsResult instanceof Object) {
          return validationsResult;
        }
        if (invalidListResult instanceof Object) {
          return invalidListResult;
        }
        if (validationsResult && !invalidListResult) {
          return true;
        }
        return false;
      },
      /* "validate.AML.middleName" method
       * "AML.middleName" validates that an input value contains only characters that are valid
       * for middle name data.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      maxLength (integer) [0]:  Optionally contain an integer, indicating
       *                          the maximum length. The integer must be greater than 0.
       *                          [Default: 40]
       *
       *                      allowSpecialChars (boolean) [1]:  A boolean value representing wether
       *                          or not to allow special characters. [Default: true]
       *
       * Returns:
       *    true  - if the input value matches the middle name format
       *    false - if the input value does not match the middle name format
       */
      middleName($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.middleName',
          keyName: 'name',
          // minLength: 2,
          maxLength: 40,
          maxRepeatedChar: ["a-zA-Z0-9 '.-", 2],
          maxRepeatExceptions: ['VIII', 'VIII.'],
          allowSpecialChars: true
        };
        let options = {
          maxLength: defaults.maxLength,
          allowSpecialChars: defaults.allowSpecialChars
        };
        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);
        const validations = {
          // minLength: [options.minLength],
          nameCharacters: [options.allowSpecialChars],
          maxLength: [options.maxLength]
        };
        let validRepeatException = false;
        options.maxRepeatExceptions.every(value => {
          const validException = $element.val().toUpperCase() === value;
          if (validException) {
            validRepeatException = true;
            return false;
          }
          return true;
        });
        if (!validRepeatException) {
          validations.maxRepeatedChar = options.maxRepeatedChar;
        }
        const validationsResult = self.getValidationResults($element, validations);
        const invalidListResult = self.getInvalidResults($element, options.ruleName, options.keyName);
        if (validationsResult instanceof Object) {
          return validationsResult;
        }
        if (invalidListResult instanceof Object) {
          return invalidListResult;
        }
        if (validationsResult && !invalidListResult) {
          return true;
        }
        return false;
      },
      /* "validate.AML.lastName" method
       * "AML.lastName" validates that an input value contains only characters that are valid
       * for last name data.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      maxLength (integer) [0]:  Optionally contain an integer, indicating
       *                          the maximum length. The integer must be greater than 0.
       *                          [Default: 40]
       *
       *                      allowSpecialChars (boolean) [1]:  A boolean value representing wether
       *                          or not to allow special characters. [Default: true]
       *
       * Returns:
       *    true  - if the input value matches the last name format
       *    false - if the input value does not match the last name format
       */
      lastName($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.lastName',
          keyName: 'name',
          minLength: 2,
          maxLength: 40,
          maxRepeatedChar: ["a-zA-Z0-9 '.-", 2],
          allowSpecialChars: true
        };
        let options = {
          maxLength: defaults.maxLength,
          allowSpecialChars: defaults.allowSpecialChars
        };
        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);
        const validations = {
          minLength: [options.minLength],
          nameCharacters: [options.allowSpecialChars],
          maxRepeatedChar: options.maxRepeatedChar,
          maxLength: [options.maxLength]
        };
        const validationsResult = self.getValidationResults($element, validations);
        const invalidListResult = self.getInvalidResults($element, options.ruleName, options.keyName);
        if (validationsResult instanceof Object) {
          return validationsResult;
        }
        if (invalidListResult instanceof Object) {
          return invalidListResult;
        }
        if (validationsResult && !invalidListResult) {
          return true;
        }
        return false;
      },
      /* "validate.AML.namePrefix" method
       * "AML.namePrefix" validates that an input value contains only characters that are valid
       * for first name data.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters. (currently not used)
       *
       * Returns:
       *    true  - if the input value matches the first name format
       *    false - if the input value does not match the first name format
       */
      namePrefix($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.namePrefix',
          keyName: 'code'
        };
        const options = Object.assign({}, defaults);
        const validations = {
          namePrefix: []
        };

        // Convert the input value to upper case.
        $element.val($element.val().toUpperCase());
        const validationsResult = self.getValidationResults($element, validations);
        const validListResult = self.getValidResults($element, options.ruleName, options.keyName);
        if (validationsResult instanceof Object) {
          return validationsResult;
        }
        if (validListResult instanceof Object) {
          return validListResult;
        }
        if (validationsResult && validListResult) {
          return true;
        }
        return false;
      },
      /* "validate.AML.nameSuffix" method
       * "AML.nameSuffix" validates that an input value contains only characters that are valid
       * for name suffix data.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters. (currently not used)
       *
       * Returns:
       *    true  - if the input value matches the name suffix format
       *    false - if the input value does not match the name suffix format
       */
      nameSuffix($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.nameSuffix',
          keyName: 'code'
        };
        const options = Object.assign({}, defaults);
        const validations = {
          nameSuffix: []
        };

        // Convert the input value to upper case.
        $element.val($element.val().toUpperCase());
        const validationsResult = self.getValidationResults($element, validations);
        const validListResult = self.getValidResults($element, options.ruleName, options.keyName);
        if (validationsResult instanceof Object) {
          return validationsResult;
        }
        if (validListResult instanceof Object) {
          return validListResult;
        }
        if (validationsResult && validListResult) {
          return true;
        }
        return false;
      },
      /* "validate.AML.nameGeneration" method
       * "AML.nameGeneration" validates that an input value contains only characters that are
       * valid for name generation data.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters. (currently not used)
       *
       * Returns:
       *    true  - if the input value matches the name generation format
       *    false - if the input value does not match the name generation format
       */
      nameGeneration($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.nameGeneration',
          keyName: 'code'
        };
        const options = Object.assign({}, defaults);
        const validations = {
          nameGeneration: []
        };

        // Convert the input value to upper case.
        $element.val($element.val().toUpperCase());
        const validationsResult = self.getValidationResults($element, validations);
        const validListResult = self.getValidResults($element, options.ruleName, options.keyName);
        if (validationsResult instanceof Object) {
          return validationsResult;
        }
        if (validListResult instanceof Object) {
          return validListResult;
        }
        if (validationsResult && validListResult) {
          return true;
        }
        return false;
      },
      /* "validate.AML.businessName" method
       * "AML.businessName" validates that an input value contains only characters that are
       * valid for business name data.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      maxLength (integer) [0]:  Optionally contain an integer, indicating
       *                          the maximum length. The integer must be greater than 0.
       *                          [Default: 60]
       *
       * Returns:
       *    true  - if the input value matches the business name format
       *    false - if the input value does not match the business name format
       */
      businessName($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.businessName',
          keyName: 'name',
          minLength: 3,
          maxLength: 60
        };
        let options = {
          maxLength: defaults.maxLength
        };
        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);
        const validations = {
          minLength: [options.minLength],
          maxLength: [options.maxLength]
        };
        const validationsResult = self.getValidationResults($element, validations);
        const invalidListResult = self.getInvalidResults($element, options.ruleName, options.keyName);
        if (validationsResult instanceof Object) {
          return validationsResult;
        }
        if (invalidListResult instanceof Object) {
          return invalidListResult;
        }
        if (validationsResult && !invalidListResult) {
          return true;
        }
        return false;
      },
      /* "validate.AML.addressLine1" method
       * "AML.addressLine1" validates that an input value is formatted in the standard pattern
       * for street address values.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      maxLength (integer) [0]:  Optionally contain an integer, indicating
       *                          the maximum length. The integer must be greater than 0.
       *                          [Default: 30]
       *
       *                      validSpecialChars (string) [1]: The second parameter can optionally
       *                          contain a string including valid special characters.
       *                          [Default: "#/.,'-"]
       *
       *                      isMailingAddress (boolean) [2]: The third parameter is a boolean value
       *                          indicating if the address is a mailing address.  [Default: true]
       *
       * Returns:
       *    true  - if the input value matches the street address format
       *    false - if the input value does not match the street address format
       */
      addressLine1($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.addressLine1',
          keyName: 'address',
          validSpecialChars: "#/.,'-",
          isMailingAddress: true,
          minLength: 3,
          maxLength: 30
        };
        let options = {
          maxLength: defaults.maxLength,
          validSpecialChars: defaults.validSpecialChars,
          isMailingAddress: defaults.isMailingAddress
        };
        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);
        const validations = {
          minLength: [options.minLength],
          intlStreetAddress: [options.validSpecialChars, options.isMailingAddress],
          maxLength: [options.maxLength]
        };
        const validationsResult = self.getValidationResults($element, validations);
        const invalidListResult = self.getInvalidResults($element, options.ruleName, options.keyName);
        if (validationsResult instanceof Object) {
          return validationsResult;
        }
        if (invalidListResult instanceof Object) {
          return invalidListResult;
        }
        if (validationsResult && !invalidListResult) {
          return true;
        }
        return false;
      },
      /* "validate.AML.addressLine2" method
       * "AML.addressLine2" validates that an input value is formatted in the standard pattern
       * for street address values.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      maxLength (integer) [0]:  Optionally contain an integer, indicating
       *                          the maximum length. The integer must be greater than 0.
       *                          [Default: 30]
       *
       *                      validSpecialChars (string) [1]: The second parameter can optionally
       *                          contain a string including valid special characters.
       *                          [Default: "#/.,'-"]
       *
       *                      isMailingAddress (boolean) [2]: The third parameter is a boolean value
       *                          indicating if the address is a mailing address.  [Default: true]
       *
       * Returns:
       *    true  - if the input value matches the street address format
       *    false - if the input value does not match the street address format
       */
      addressLine2($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.addressLine2',
          keyName: 'address',
          validSpecialChars: "#/.,'-",
          isMailingAddress: true,
          minLength: 3,
          maxLength: 30
        };
        let options = {
          maxLength: defaults.maxLength,
          validSpecialChars: defaults.validSpecialChars,
          isMailingAddress: defaults.isMailingAddress
        };
        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);
        const validations = {
          minLength: [options.minLength],
          intlStreetAddress: [options.validSpecialChars, options.isMailingAddress],
          maxLength: [options.maxLength]
        };
        const validationsResult = self.getValidationResults($element, validations);
        const invalidListResult = self.getInvalidResults($element, options.ruleName, options.keyName);
        if (validationsResult instanceof Object) {
          return validationsResult;
        }
        if (invalidListResult instanceof Object) {
          return invalidListResult;
        }
        if (validationsResult && !invalidListResult) {
          return true;
        }
        return false;
      },
      /* "validate.AML.cityCounty" method
       * "AML.cityCounty" validates that an input value is formatted in the standard pattern for
       * a city or county name values.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      region (string) [0]: Optionally contain one of three string values for
       *                          the country/international ("US", "CANADA", "INTERNATIONAL").
       *                          [Default: "US"]
       *
       *                      maxLength (integer) [1]: Optionally contain an integer, indicating the
       *                          maximum length. The integer must be greater than 0. [Default: 19]
       *
       * Returns:
       *    true  - if the input value matches the city/county format
       *    false - if the input value does not match the city/county format
       */
      cityCounty($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.cityCounty',
          keyName: 'city',
          minLength: 3,
          maxLength: 19,
          region: STRINGS.UNITED_STATES,
          validCharacters: null
        };
        let options = {
          region: defaults.region,
          maxLength: defaults.maxLength,
          validCharacters: defaults.validCharacters
        };
        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);
        if (options.region === STRINGS.UNITED_STATES && !options.validCharacters) {
          options.validCharacters = '.-';
        }
        const validations = {
          minLength: [options.minLength],
          cityCounty: [options.region, options.validCharacters],
          maxLength: [options.maxLength]
        };
        if (options.region.toUpperCase() !== STRINGS.UNITED_STATES) {
          validations.minLength = [2];
        }
        const validationsResult = self.getValidationResults($element, validations);
        const invalidListResult = self.getInvalidResults($element, options.ruleName, options.keyName);
        if (validationsResult instanceof Object) {
          let result = {};
          if (options.region.toUpperCase() === STRINGS.UNITED_STATES && self.language === STRINGS.ENGLISH_LANGUAGE) {
            result.errorMessage = STRINGS.CITY_COUNTY_EN;
          } else if (options.region.toUpperCase() === STRINGS.UNITED_STATES && self.language === STRINGS.SPANISH_LANGUAGE) {
            result.errorMessage = STRINGS.CITY_COUNTY_ES;
          }
          result = Object.assign({}, validationsResult, result);
          return result;
        }
        if (invalidListResult instanceof Object) {
          return invalidListResult;
        }
        if (validationsResult && !invalidListResult) {
          return true;
        }
        return false;
      },
      /* "validate.AML.stateCode" method
       * "AML.stateCode" validates that an input value is formatted in the standard pattern for
       * state or province code values.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      region (string) [0]: Optionally contain one of three string values for
       *                          the country/international ("US", "CANADA", "INTERNATIONAL").
       *                          [Default: "US"]
       *
       * Returns:
       *    true  - if the input value matches the state code format
       *    false - if the input value does not match the state code format
       */
      stateCode($element, ruleParams) {
        let validListResult = false;
        const defaults = {
          ruleName: 'AML.stateCode',
          keyName: 'code',
          minLength: 2,
          maxLength: 2,
          region: STRINGS.UNITED_STATES
        };
        let options = {
          region: defaults.region
        };
        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);
        const validations = {
          minLength: [options.minLength],
          stateCode: [options.region],
          maxLength: [options.maxLength]
        };

        // Convert the input value to upper case.
        $element.val($element.val().toUpperCase());
        if (options.region === STRINGS.UNITED_STATES) {
          validListResult = self.getValidResults($element, options.ruleName, options.keyName);
        }
        if (options.region === STRINGS.CANADA) {
          validations.stateCode = [STRINGS.CANADA];
          validListResult = true;
        }
        if (options.region === STRINGS.INTERNATIONAL) {
          validations.stateCode = [STRINGS.INTERNATIONAL];
          validations.maxLength = [5];
          validListResult = true;
        }
        const validationsResult = self.getValidationResults($element, validations);
        if (validationsResult instanceof Object) {
          return validationsResult;
        }
        if (validListResult instanceof Object) {
          return validListResult;
        }
        if (validationsResult && validListResult) {
          return true;
        }
        return false;
      },
      /* "validate.AML.zipCode" method
       * "AML.zipCode" validates that an input value is formatted in the standard pattern for a
       * US zip code (e.g., 12345 or 12345-6789).
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      region (string) [0]: Optionally contain one of three string values for
       *                          the country/international ("US", "CANADA", "INTERNATIONAL").
       *                          [Default: "US"]
       *
       *                      exceptionRule (string) [1]: A string value for LOB exceptions
       *                          (e.g. "GWIM", "US TRUST", "CED"). [Default: "GWIM"]
       *
       * Returns:
       *    true  - if the input value matches the zip code format
       *    false - if the input value does not match the zip code format
       */
      zipCode($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.zipCode',
          minLength: 5,
          maxLength: 5,
          region: STRINGS.UNITED_STATES,
          exceptionRule: STRINGS.GWIM,
          gwimExceptions: ['44444', '12345'],
          invalidZipCodes: ['00000', '11111', '22222', '33333', '55555', '66666', '77777', '88888', '99999', '54321']
        };
        let options = {
          region: defaults.region,
          exceptionRule: defaults.exceptionRule
        };
        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);
        if (options.exceptionRule.toUpperCase() !== STRINGS.GWIM) {
          options.invalidZipCodes = options.invalidZipCodes.concat(options.gwimExceptions);
        }
        let validations = {
          minLength: [options.minLength],
          zip: [options.region],
          isNotValue: [options.invalidZipCodes],
          maxLength: [options.maxLength]
        };
        if (options.region.toUpperCase() !== STRINGS.UNITED_STATES) {
          validations = {
            minLength: [3],
            maxLength: [7]
          };

          // Convert the input value to upper case.
          $element.val($element.val().toUpperCase());
          if (options.region.toUpperCase() === STRINGS.INTERNATIONAL) {
            validations.maxLength = [10];
          }
        }
        const validationsResult = self.getValidationResults($element, validations);
        if (validationsResult instanceof Object) {
          return validationsResult;
        }
        if (validationsResult) {
          return true;
        }
        return false;
      },
      /* "validate.AML.countryCode" method
       * "AML.countryCode" validates that an input value is formatted in the standard pattern
       * for ISO-2 or ISO-3 country code values.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      isoFormat (string) [0]: Optionally contain a string value representing
       *                          the format of the country code (e.g. "ISO-2", "ISO-3")
       *                          [Default: "ISO-2"]
       *
       * Returns:
       *    true  - if the input value matches the country code format
       *    false - if the input value does not match the country code format
       */
      countryCode($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.countryCode',
          minLength: 2,
          maxLength: 2,
          isoFormat: STRINGS.ISO2
        };
        let options = {
          isoFormat: defaults.isoFormat
        };
        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);
        const validations = {
          minLength: [options.minLength],
          countryCode: [options.isoFormat],
          maxLength: [options.maxLength]
        };

        // Convert the input value to upper case.
        $element.val($element.val().toUpperCase());
        if (options.isoFormat.toUpperCase() !== STRINGS.ISO2) {
          validations.maxLength = [3];
          if (options.isoFormat.toUpperCase() === STRINGS.ISO3166) {
            validations.minLength = [1];
          }
        }
        const validListResult = self.getValidResults($element, options.ruleName, options.isoFormat);
        const validationsResult = self.getValidationResults($element, validations);
        if (validationsResult instanceof Object) {
          return validationsResult;
        }
        if (validListResult instanceof Object) {
          return validListResult;
        }
        if (validationsResult && validListResult) {
          return true;
        }
        return false;
      },
      /* "validate.AML.dateOfBirth" method
       * "AML.dateOfBirth" validates that an input value is formatted in the standard pattern
       * for a US date pattern (e.g., 01/01/1970) and the date is a valid date of birth.
       *
       * Parameters:
       *    $dateElement      - a jQuery reference to the date input element whose value is
       *                        being checked
       *    ruleParams        - an array holding any rule parameters.
       *
       *                        dateType (string) [0]: Optionally contains a date type string
       *                            (either "fullDate" ["mm/dd/yyyy"] or "monthYearDate"
       *                            ["mm/yyyy"]). [Default: "fullDate"]
       *
       *                        rangeException (boolean) [1]: A boolean value for date range
       *                            exception (e.g. true - if allow birth dates older than 125
       *                            years). [Default: false]
       *
       * Returns:
       *    true  - if the date value matches a valid date of birth format
       *    false - if the date value does not match a valid date of birth format
       */
      dateOfBirth($dateElement, ruleParams) {
        const defaults = {
          ruleName: 'AML.dateOfBirth',
          dateType: 'fullDate',
          rangeException: false,
          minDateRange: '-125y',
          maxDateRange: '0d'
        };
        let options = {
          dateType: defaults.dateType,
          rangeException: defaults.rangeException
        };
        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);
        let validations = {
          dateFormat: [options.dateType],
          dateRange: [options.minDateRange, options.maxDateRange, options.dateType]
        };

        // if the minimum date range exception is set to true, only validate the end date
        if (options.rangeException === true) {
          validations = {
            dateFormat: [options.dateType],
            endDate: [options.maxDateRange, options.dateType]
          };
        }
        const validationsResult = self.getValidationResults($dateElement, validations);
        if (validationsResult instanceof Object) {
          return validationsResult;
        }
        if (validationsResult) {
          return true;
        }
        return false;
      },
      /* "validate.AML.ssn" method
       * "AML.ssn" validates that an input value is formatted in the standard pattern for a
       * Social Security Number (e.g., 123-45-6789).
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      allowExceptions (boolean) [0]: A boolean value to allow invalid SSN
       *                          exceptions. [Default: false]
       *
       * Returns:
       *    true  - if the input value matches the SSN pattern
       *    false - if the input value does not match the SSN pattern
       */
      ssn($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.ssn',
          allowExceptions: false,
          invalidSSNExceptions: [/^000000000$/],
          invalidSSNPatterns: [/^300000000$/, /^666(\d{2})(\d{4})$/, /^000(\d{2})([1-9]\d{3})$/, /^000([1-9]\d{1})(\d{4})$/, /^(\d{3})00([1-9]\d{3})$/, /^([1-9]\d{2})00(\d{4})$/, /^(\d{3})([1-9]\d{1})0000$/, /^([1-9]\d{2})(\d{2})0000$/, /^9(\d{2})89(\d{4})$/, /^9(\d{2})[0-6](\d{1})(\d{4})$/]
        };
        let options = {
          allowExceptions: defaults.allowExceptions
        };
        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);
        const validations = {
          ssn: []
        };
        const validationsResult = self.getValidationResults($element, validations);
        const elementValue = $element.val().replace(/\D/g, '');
        let invalidSSNResult = false;
        if (!options.allowExceptions) {
          options.invalidSSNPatterns = options.invalidSSNPatterns.concat(options.invalidSSNExceptions);
        }
        if (options.invalidSSNPatterns.length !== 0) {
          options.invalidSSNPatterns.every(invalidSSNPattern => {
            invalidSSNResult = invalidSSNPattern.test(elementValue);
            if (invalidSSNResult) {
              return false;
            }
            return true;
          });
        }
        if (validationsResult instanceof Object) {
          return validationsResult;
        }
        if (validationsResult && !invalidSSNResult) {
          return true;
        }
        return false;
      },
      /* "validate.AML.tin" method
       * "AML.tin" validates that an input value is formatted in the standard pattern for a
       * Taxpayer Identification Number - Business (e.g., 123-45-6789).
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      allowExceptions (boolean) [0]: A boolean value to allow invalid TIN
       *                          exceptions. [Default: false]
       *
       * Returns:
       *    true  - if the input value matches the TIN pattern
       *    false - if the input value does not match the TIN pattern
       */
      tin($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.tin',
          allowExceptions: false,
          invalidTINExceptions: [/^000000000$/],
          invalidTINPatterns: [/^300000000$/, /^400000000$/, /^500000000$/, /^000(\d{2})([1-9]\d{3})$/, /^000([1-9]\d{1})(\d{4})$/, /^(\d{3})00([1-9]\d{3})$/, /^([1-9]\d{2})00(\d{4})$/, /^(\d{3})([1-9]\d{1})0000$/, /^([1-9]\d{2})(\d{2})0000$/, /^9(\d{2})93(\d{4})$/, /^9(\d{2})[7|8|9](\d{1})(\d{4})$/]
        };
        let options = {
          allowExceptions: defaults.allowExceptions
        };
        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);
        const validations = {
          ssn: []
        };
        const validationsResult = self.getValidationResults($element, validations);
        const elementValue = $element.val().replace(/\D/g, '');
        let invalidTINResult = false;
        if (!options.allowExceptions) {
          options.invalidTINPatterns = options.invalidTINPatterns.concat(options.invalidTINExceptions);
        }
        if (options.invalidTINPatterns.length !== 0) {
          options.invalidTINPatterns.every(invalidTINPattern => {
            invalidTINResult = invalidTINPattern.test(elementValue);
            if (invalidTINResult) {
              return false;
            }
            return true;
          });
        }
        if (validationsResult instanceof Object) {
          return validationsResult;
        }
        if (validationsResult && !invalidTINResult) {
          return true;
        }
        return false;
      },
      /* "validate.AML.itin" method
       * "AML.itin" validates that an input value is formatted in the standard pattern for a
       * Taxpayer Identification Number - Individual (e.g., 123-45-6789).
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      allowExceptions (boolean) [0]: A boolean value to allow valid ITIN
       *                          exceptions. [Default: false]
       *
       * Returns:
       *    true  - if the input value matches the ITIN pattern
       *    false - if the input value does not match the ITIN pattern
       */
      itin($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.itin',
          allowExceptions: false,
          validITINExceptions: [/^000000000$/],
          validITINPatterns: [/^9(\d{2})([7][0-9]|[8][0-8]|[9][0-2,4-9])(\d{4})$/]
        };
        let options = {
          allowExceptions: defaults.allowExceptions
        };
        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);
        const validations = {
          ssn: []
        };
        const validationsResult = self.getValidationResults($element, validations);
        const elementValue = $element.val().replace(/\D/g, '');
        let validITINResult = false;
        if (options.allowExceptions === true) {
          options.validITINExceptions.every(validITINException => {
            validITINResult = validITINException.test(elementValue);
            if (validITINResult) {
              return false;
            }
            return true;
          });
        }
        if (!validITINResult && options.validITINPatterns.length !== 0) {
          options.validITINPatterns.every(validITINPattern => {
            validITINResult = validITINPattern.test(elementValue);
            if (validITINResult) {
              return false;
            }
            return true;
          });
        }
        if (validationsResult instanceof Object) {
          return validationsResult;
        }
        if (validationsResult && validITINResult) {
          return true;
        }
        return false;
      },
      /* "validate.AML.ein" method
       * "AML.ein" validates that an input value is formatted in the standard pattern for a
       * Employer Identification Number (e.g., 12-3456789).
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array holding any rule parameters.
       *
       *                      allowExceptions (boolean) [0]: A boolean value to allow valid EIN
       *                          exceptions. [Default: false]
       *
       * Returns:
       *    true  - if the input value matches the EIN pattern
       *    false - if the input value does not match the EIN pattern
       */
      ein($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.ein',
          allowExceptions: false,
          invalidEINExceptions: [/^000000000$/],
          invalidEINPatterns: []
        };
        let options = {
          allowExceptions: defaults.allowExceptions
        };
        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);
        const validations = {
          ein: []
        };
        const validationsResult = self.getValidationResults($element, validations);
        const elementValue = $element.val().replace(/\D/g, '');
        let invalidEINResult = false;
        if (!options.allowExceptions) {
          options.invalidEINPatterns = options.invalidEINPatterns.concat(options.invalidEINExceptions);
        }
        if (options.invalidEINPatterns.length !== 0) {
          options.invalidEINPatterns.every(invalidEINPattern => {
            invalidEINResult = invalidEINPattern.test(elementValue);
            if (invalidEINResult) {
              return false;
            }
            return true;
          });
        }
        if (validationsResult instanceof Object) {
          return validationsResult;
        }
        if (validationsResult && !invalidEINResult) {
          return true;
        }
        return false;
      }
    };
    return AML;
  }

  /**
   * @method getValidationResults
   * @description Executes the validation with given parameters for each rule within the validations
   * object.
   * @param {Object} $element - the jQuery input element used to run each validation against
   * @param {Object} validations - an object containing each rule and given parameters
   * @returns {Object|Boolean} validationResult
   */
  getValidationResults($element, validations) {
    let validationResult = true;
    Object.keys(validations).every(ruleName => {
      const ruleParams = validations[ruleName];
      validationResult = this.validator.fn.validate[ruleName]($element, ruleParams);
      if (!validationResult) {
        validationResult = {
          ruleName,
          ruleParams
        };
        return false;
      }
      return true;
    });
    return validationResult;
  }

  /**
   * @method getInvalidResults
   * @description Checks to see if the $element value matches a list of invalid values.
   * @param {Object} $element - the jQuery input element used to run each validation against
   * @param {String} ruleName - name of the rule to retrieve the list of invalid values
   * @param {String} keyName - a string representing the key name for a invalid value
   * @returns {Boolean} invalidResult
   */
  getInvalidResults($element, ruleName, keyName, matchType, caseSensitive = false) {
    let invalidResult = false;
    const elementValue = $element.val();
    const invalidValues = this.importer.RULES[ruleName].list;
    if (invalidValues && invalidValues.length !== 0) {
      invalidValues.every(invalidValue => {
        let exactMatchType = invalidValue.type === STRINGS.EXACT_MATCH;
        let containsType = invalidValue.type === STRINGS.CONTAINS;
        if (matchType) {
          exactMatchType = matchType === STRINGS.EXACT_MATCH;
          containsType = matchType === STRINGS.CONTAINS;
        }
        if (exactMatchType) {
          invalidResult = caseSensitive ? elementValue === invalidValue[keyName] : elementValue.toUpperCase() === invalidValue[keyName].toUpperCase();
        } else if (containsType) {
          invalidResult = caseSensitive ? elementValue.indexOf(invalidValue[keyName]) >= 0 : elementValue.toUpperCase().indexOf(invalidValue[keyName].toUpperCase()) >= 0;
        }
        if (invalidResult) {
          invalidResult = {
            ruleName,
            ruleParams: [elementValue]
          };
          return false;
        }
        return true;
      });
    } else {
      if (window.console && console.warn) {
        console.warn(`Sparta Validation: ${ruleName} list empty.`);
      }
      return {
        ruleName,
        ruleParams: [elementValue]
      };
    }
    return invalidResult;
  }

  /**
   * @method getValidResults
   * @description Checks to see if the $element value matches a list of valid values.
   * @param {Object} $element - the jQuery input element used to run each validation against
   * @param {String} ruleName - name of the rule to retrieve the list of valid values
   * @param {String} keyName - a string representing the key name for a valid value
   * @returns {Boolean} validResult
   */
  getValidResults($element, ruleName, keyName, caseSensitive = false) {
    let validResult = false;
    const elementValue = $element.val();
    const validValues = this.importer.RULES[ruleName].list;
    if (validValues && validValues.length !== 0) {
      validValues.every(validValue => {
        if (caseSensitive) {
          validResult = elementValue === validValue[keyName];
        } else {
          validResult = elementValue.toUpperCase() === validValue[keyName].toUpperCase();
        }
        if (validResult) {
          return false;
        }
        return true;
      });
    } else if (window.console && console.warn) {
      console.warn(`Sparta Validation: ${ruleName} list empty.`);
    }
    if (validResult) {
      return true;
    }
    return {
      ruleName,
      ruleParams: [elementValue]
    };
  }
}
const AMLValidations = new AMLValidationRules();

/**
 * @const utilityNamespace
 * @description namespace for Validation Rules Importer.
 */
const utilityNamespace$1 = 'spartaValidationFramework';

/**
 * @const EVENTS
 * @description constant for storing namespaced event strings
 */
const EVENTS$1 = {
  RULES_IMPORT_COMPLETE_EVENT: `RULES_IMPORT_COMPLETE.${utilityNamespace$1}`,
  RULES_IMPORT_SUCCESS_EVENT: `RULES_IMPORT_SUCCESS.${utilityNamespace$1}`,
  RULES_IMPORT_ERROR_EVENT: `RULES_IMPORT_ERROR.${utilityNamespace$1}`
};

/**
 * @const RULES
 * @description constant for storing validation rules lists object
 */
const RULES = {
  'AML.addressLine1': {
    filename: 'invalid-address-list.json'
  },
  'AML.addressLine2': {
    filename: 'invalid-address-list.json'
  },
  'AML.businessName': {
    filename: 'invalid-business-list.json'
  },
  'AML.cityCounty': {
    filename: 'invalid-city-list.json'
  },
  'AML.countryCode': {
    filename: 'country-code-list.json'
  },
  'AML.firstName': {
    filename: 'invalid-firstname-list.json'
  },
  'AML.lastName': {
    filename: 'invalid-lastname-list.json'
  },
  'AML.middleName': {
    filename: 'invalid-middlename-list.json'
  },
  'AML.nameGeneration': {
    filename: 'name-generation-list.json'
  },
  'AML.namePrefix': {
    filename: 'name-prefix-list.json'
  },
  'AML.nameSuffix': {
    filename: 'name-suffix-list.json'
  },
  'AML.stateCode': {
    filename: 'state-code-list.json'
  },
  pending: [],
  complete: []
};

/**
 * ValidationRulesImporter for SpartaValidationFramework
 */
class ValidationRulesImporter {
  /**
   * Starts sparta subs
   */
  constructor() {
    spartaEventsUtility.sub(this.EVENTS.RULES_IMPORT_SUCCESS_EVENT, rule => {
      this.completedRulesEvent(rule.data);
    }, true);
  }

  /**
   * Initializiation method.
   * @returns {Object} Rules object.
   */
  init() {
    return this.RULES;
  }

  /**
   * @property {Object} EVENTS() exposes the events list constant
   * @description getter for EVENTS constant
   * @returns {Object} EVENTS
   */
  get EVENTS() {
    // eslint-disable-line class-methods-use-this
    return EVENTS$1;
  }

  /**
   * @property {Object} RULES() exposes validation rules filenames list constant
   * @description getter for RULES constant
   * @returns {Object} RULES
   */
  get RULES() {
    // eslint-disable-line class-methods-use-this
    return RULES;
  }

  /**
   * @method getValidationRules
   * @description Retrieves JSON files associated with a rule from the data folder.
   * @param {string} rule - rule name to retrieve the associated filename from the RULES object
   * @returns {void}
   */
  getValidationRules(rule) {
    this.language = spartaGlobalFunctions.getLanguage();
    const {
      filename
    } = this.RULES[rule];

    // generate a new DataAssetLoader instance
    const ruleAssetLoader = new DataAssetLoader('@sparta.global.utilities/sparta-validation-framework-utility', `data/${this.language}`);

    // load the relevant rule JSON file, which triggers success and failure events
    // so that the utility can subscribe to them
    const load = ruleAssetLoader.loadAsset(filename);
    spartaEventsUtility.sub(load.success, evtData => {
      // trigger the original ajaxSuccess event
      this.ajaxSuccess(evtData.data.xhr, rule);
    }, true);
    spartaEventsUtility.sub(load.failure, evtData => {
      // log out an error for debugging the issue
      this.ajaxError(evtData.data.xhr, rule);
    }, true);
  }

  /**
   * @method getAllPendingRules
   * @description Retrieves all pending validation rules.
   * @returns {void}
   */
  getAllPendingRules() {
    this.RULES.pending.forEach(rule => {
      const isCompleted = this.RULES.complete.includes(rule);
      if (!isCompleted) {
        this.getValidationRules(rule);
      }
    });
  }

  /**
   * @method ajaxSuccess
   * @description Adds the retrieved list to the rule.
   * @param {object} data - the data returned by the jQuery $.ajax() / Sparta Transport method
   * @param {string} rule - name of the rule to assign the data to it's list property
   * @returns {void}
   */
  ajaxSuccess(data, rule) {
    this.RULES[rule].list = data;
    spartaEventsUtility.pub(this.EVENTS.RULES_IMPORT_SUCCESS_EVENT, `${rule}`);
  }

  /**
   * @method ajaxError
   * @description Sets the list property for the rule to an empty array.
   * @param {object} xhr - the xhr object returned by the jQuery $.ajax() / Sparta Transport method
   * @param {string} rule - name of the rule to assign the empty array to it's list property
   * @returns {void}
   */
  ajaxError(xhr, rule) {
    this.RULES[rule].list = [];
    spartaEventsUtility.pub(this.EVENTS.RULES_IMPORT_ERROR_EVENT, {
      xhr,
      rule
    });
    if (window.console && console.warn) {
      console.warn(`Sparta Validation: ${rule} AJAX Error.`, xhr);
    }
  }

  /**
   * @method completedRulesEvent
   * @description Checks to see if all rules have been imported, triggers completed EVENT.
   * @param {string} rule - rule name add to complete list
   * @returns {void}
   */
  completedRulesEvent(rule) {
    let newRullAdded = false;

    // Checks the pending array and complete array for the new rule.
    // If a pending rule is not in the complete array then it is added
    if (this.RULES.pending.includes(rule) && !this.RULES.complete.includes(rule)) {
      this.RULES.complete.push(rule);
      newRullAdded = true;
    }

    // If pending and complete are the same size and every pending element is in the complete array
    // then allRulesComplete is true.
    this.allRulesComplete = this.RULES.pending.length === this.RULES.complete.length && this.RULES.pending.every(element => {
      const isCompleted = this.RULES.complete.includes(element);
      return isCompleted;
    });

    // newRullAdded flag will prevent the pub from firing multiple times if a rule has already been
    // added.
    if (this.allRulesComplete && newRullAdded) {
      spartaEventsUtility.pub(this.EVENTS.RULES_IMPORT_COMPLETE_EVENT, this.RULES.complete);
    }
  }
}
const validationRulesImporter = new ValidationRulesImporter();

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * @name        Sparta Validation Framework
 * @file        sparta-validation-framework.js
 * @description Provides standard validation logic for inputs
 * @classdesc   Provides standard validation logic for inputs
 * @global      Creates a a global SpartaValidator object
 * @author      Kerry Boyce
 * @copyright   © 2016 Bank of America Corporation. All rights reserved.
 *
 * The Sparta Validation Framework is used to create, present, and control a common validation
 * framework functionality, in order to support a standard form validation experience across the
 * Bank of America websites.  The pre-defined validation rules each contain granular logic,
 * intended to check for a single validation.  By keeping the validations small, the rules become
 * more reusable and allow for targeted error messaging, while supporting more complex logic
 * through combining multiple rules together for an individual field.
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */ // prevent issues with other JS files omitting their last semi-colon

/**
 * @const utilityNamespace
 * @description namespace for Validation Rules Importer.
 */
const utilityNamespace = 'spartaValidationFramework';

/**
 * @const EVENTS
 * @description constant for storing namespaced event strings
 */
const EVENTS = {
  VALIDATION_FAILURE_EVENT: `FAIL_INPUT_VALIDATION.${utilityNamespace}`,
  VALIDATION_SUCCESS_EVENT: `PASS_INPUT_VALIDATION.${utilityNamespace}`,
  REQUIRED_FIELD_FAILURE_EVENT: `FAIL_REQUIRED_FIELD_VALIDATION.${utilityNamespace}`,
  REQUIRED_FIELD_SUCCESS_EVENT: `PASS_REQUIRED_FIELD_VALIDATION.${utilityNamespace}`,
  ...validationRulesImporter.EVENTS
};

/**
 * @const data
 * @description constant for storing validation data containing the globals & oValidationRules
 * object(s)
 */
const data = {
  globals: {
    VALIDATOR_NAMESPACE: 'SpartaValidator',
    INPUT_ERROR_BASE_CLASS: 'spa-error-form-input',
    LABEL_ERROR_BASE_CLASS: 'spa-error-form-label',
    VALIDATION_ERROR_SUFFIX: '--validation',
    REQUIRED_ERROR_SUFFIX: '--required',
    REQUIRED_FIELD_RULE_NAME: 'required-field',
    VALIDATION_FAILURE_EVENT: EVENTS.VALIDATION_FAILURE_EVENT,
    VALIDATION_SUCCESS_EVENT: EVENTS.VALIDATION_SUCCESS_EVENT,
    REQUIRED_FIELD_FAILURE_EVENT: EVENTS.REQUIRED_FIELD_FAILURE_EVENT,
    REQUIRED_FIELD_SUCCESS_EVENT: EVENTS.REQUIRED_FIELD_SUCCESS_EVENT
  },
  /* data.oValidationRules - container to store the input fields and their validation rules and
    messages that are registered by the page */
  oValidationRules: {}
};
class SpartaValidator {
  init() {
    const validator = this;
    this.language = spartaStoreUtility.getData('lang');
    const messages = spartaStoreUtility.getData('spaParams').global[this.language.split('-')[0]];
    this.messageList = messages.validationErrors;
    this.RULES = validationRulesImporter.RULES;
    this.AMLValidationRules = AMLValidations.init(validator, validationRulesImporter);

    /* SpartaValidator.fn - library methods */
    this.fn = {
      /* "fn.helper" methods - common "utility" functions that are used in multiple places in the
       * library */
      helper: {
        /* "helper.isEmptyField" method
         * "isEmptyField" checks to see if an input field has no value.
         *
         * Parameters:
         *    $element - a jQuery reference to the input element that is being checked
         *
         * Returns:
         *    true  - if the field is empty, undefined, or matches the placeholder value
         *    false - if the field contains a value
         */
        isEmptyField($element) {
          // If checkbox fieldset, find if any input is checked.
          if ($element[0].nodeName.toLowerCase() === 'fieldset') {
            return $element.find('input').filter(':checked').length === 0;
          }
          const sInputVal = $element.val() && $element.val().trim();
          const sPlaceholderVal = $element.attr('placeholder');
          return sInputVal === '' || sInputVal === null || typeof sPlaceholderVal !== 'undefined' && sPlaceholderVal === sInputVal;
        },
        /* "helper.isValidStringValue" method
         * "isValidStringValue" checks to see if an String variable contains a valid, non-empty
         * string value.
         *
         * Parameters:
         *    string - the string value that is being checked
         *
         * Returns:
         *    true  - if the string is has at least one character in it
         *    false - if the field is empty, undefined, or null
         */
        isValidStringValue(string) {
          return string !== undefined && string !== null && string !== '';
        },
        /* "helper.matchesPattern" method
         * "matchesPattern" is a basic regular expression pattern check for input values.  It
         * compares the value of the input to a supplied regular expression pattern
         *
         * Parameters:
         *    inputValue        - a jQuery reference to the input element whose value is being
         *                        checked OR a string value
         *    regexValidPattern - the RegExp value to test the input value against
         *
         * Returns:
         *    true  - if the value matches the RegExp pattern
         *    false - if the value does not match the RegExp pattern
         */
        matchesPattern(inputValue, regexValidPattern) {
          let bMatchesPattern = true;

          /* if "inputValue" is a string, test the pattern against it */
          if (typeof inputValue === 'string') {
            bMatchesPattern = regexValidPattern.test(inputValue);
          } else if (inputValue && (inputValue instanceof $ || inputValue.constructor.prototype.jquery)) {
            /* if "inputValue" is a jQuery object, retrieve the value and test the pattern
                against it */
            bMatchesPattern = regexValidPattern.test(inputValue.val());
          } else {
            /* if "inputValue" isn't in a valid format, return false */
            return false;
          }
          return bMatchesPattern;
        },
        /* "helper.isValidDate" method
         * "isValidDate" checks a date input to make sure that the value is an actual Date value,
         * but does check formatting or any kind of "date range" validation.  Since the JavaScript
         * Date object will attempt to "fix" a date value that is not an actually date (e.g.,
         * 13/01/2000 is "fixed" to 01/01/2001) the code passes the entered value into the Date
         * constructor and compares the result with the original value.  If "Date()" had to alter
         * the value, then the entered value was not a valid date.
         *
         * Parameters:
         *    sDateInputVal - a string value that represents the date value entered by the user
         *
         * Returns:
         *    true  - if the date is a valid Date value
         *    false - if the date is not a valid Date value
         */
        isValidDate(sDateInputVal) {
          let bIsValidDate = true;

          /* Return false if the input can not be used to create a valid Date object */
          if (isNaN(Date.parse(sDateInputVal))) {
            bIsValidDate = false;
          } else {
            /* Create a Date object from the input and get a formatted date string from the value */
            const dComputedDate = new Date(sDateInputVal);
            const sComputedDate = validator.fn.helper.formatDate(dComputedDate);

            /* Return false if the derived date string does not match the input date string */
            if (sDateInputVal !== sComputedDate) {
              bIsValidDate = false;
            }
          }

          /* Return true if the input date string passes the validation tests */
          return bIsValidDate;
        },
        /* "helper.formatMonthDay" method
         * "formatMonthDay" takes a month or day value, and prepends a "0" to it, if the value is
         * less than 10 in order to enforce a 2-digit standard display of month and day values in
         * dates
         *
         * Parameters:
         *    dateValue - a string or integer value representing a month or day
         *
         * Returns:
         *    - either the original month/day value or the original vale with a "0" prepended
         */
        formatMonthDay(dateValue) {
          const iDateValue = parseInt(dateValue, 10);
          return iDateValue < 10 ? `0${iDateValue}` : dateValue;
        },
        /* "helper.convertMonthYearToFullDate" method
         * "convertMonthYearToFullDate" takes a string or array representation of a "month/year"
         * date, and formats it in a full date format (i.e., mm/dd/yyyy), using a passed in value
         * for the day element
         *
         * Parameters:
         *    dateValue - a JavaScript a string representation of a date, in the format
         *                "mm/yyyy" or an array containing values for the month and year
         *    sDayValue - a string representation of the "day" value for the new date
         *
         * Returns:
         *    - a string representation of the date in the following format:   mm/dd/yyyy
         */
        convertMonthYearToFullDate(dateValue, sDayValue) {
          let aDateInputVals = [];

          /* if "dateValue" is an Array, copy the values into "aDateInputVals" */
          if (dateValue instanceof Array) {
            aDateInputVals = dateValue;
          } else if (typeof dateValue === 'string') {
            /* if "dateValue" is a string, split the string into the month and year values into
                "aDateInputVals" */
            aDateInputVals = dateValue.split('/');
          } else {
            /* if "dateValue" isn't in a valid format, return an error message */
            return '-Invalid Date-';
          }

          /* insert the day value into the date elements array */
          aDateInputVals.splice(1, 0, sDayValue);

          /* Return the converted date string */
          return aDateInputVals.join('/');
        },
        /* "helper.formatDate" method
         * "formatDate" takes a JavaScript date value, and formats according to the passed in date
         * type, pre-pending "0" to any single digit month or day values
         *
         * Parameters:
         *    dateValue - a JavaScript Date object, a string representation of a date, in the
         *                format "mm/dd/yyyy", or an array containing values for the month, day,
         *                and year
         *    sDateType - an string containing a value to identify the date format that the field
         *                value must match.  Currently, the valid options are:
         *
         *                - "fullDate" - value must be a valid date in the following format:
         *                  mm/dd/yyyy
         *                - "monthYearDate" - value must be a valid date in the following format:
         *                  mm/yyyy
         *
         * Returns:
         *    - a string representation of the date in the following format:   mm/dd/yyyy
         */
        formatDate(dateValue, sDateType) {
          let aDateInputVals = [];

          /* if "dateValue" is an Array, copy the values into "aDateInputVals" */
          if (dateValue instanceof Array) {
            aDateInputVals = dateValue;
          } else if (dateValue instanceof Date) {
            /* if "dateValue" is a Date, retrieve the month, day, and year values and add them to
                "aDateInputVals" */
            aDateInputVals.push(dateValue.getMonth() + 1);
            aDateInputVals.push(dateValue.getDate());
            aDateInputVals.push(dateValue.getFullYear());
          } else if (typeof dateValue === 'string') {
            /* if "dateValue" is a string, split the string into the month, day, and year values
                into "aDateInputVals" */
            aDateInputVals = dateValue.split('/');
          } else {
            /* if "dateValue" isn't in a valid format, return an error message */
            return '-Invalid Date-';
          }

          /* prepend "0" to the month value, if needed */
          aDateInputVals[0] = validator.fn.helper.formatMonthDay(aDateInputVals[0]);

          /* Format the date, based on the date type */
          let sFormattedDate = '';
          if (sDateType === 'monthYearDate') {
            sFormattedDate = `${aDateInputVals[0]}/${aDateInputVals[aDateInputVals.length - 1]}`;
          } else {
            /* prepend "0" to the day value, if needed */
            aDateInputVals[1] = validator.fn.helper.formatMonthDay(aDateInputVals[1]);
            sFormattedDate = aDateInputVals.join('/');
          }

          /* Return the formatted date string */
          return sFormattedDate;
        },
        /* "helper.getOffsetDate" method
         * "getOffsetDate" takes a date offset and applies it to the current date, in order to
         * come up target date.  If the date is in "mm/yyyy" format, the day is set to "01", for
         * comparisons. The offset uses the the Datepicker convention, where the day, month, and
         * year offsets are represented by "d", "m", and "y" in, each preceded by a positive or
         * negative increment value (e.g., "7d" = 7 days after the current date, "-1m" = one month
         * prior to the current date, "-1d1y" = one day less than a year after the current date,
         * etc.)
         *
         * Parameters:
         *    sDateOffset - a Datepicker formatted offset value
         *    sDateType   - an string containing a value to identify the date format that the field
         *                  value must match.  Currently, the valid options are:
         *
         *                  - "fullDate" - value must be a valid date in the following format:
         *                    mm/dd/yyyy
         *                  - "monthYearDate" - value must be a valid date in the following format:
         *                    mm/yyyy
         *
         * Returns:
         *    dTargetDate - a JavaScript Date object, representing the target date, derived from
         *                  the current date and the date offset
         */
        getOffsetDate(sDateOffset, sDateType) {
          /* Create new Date object for the current date and zero out its time values */
          const dTargetDate = new Date();
          dTargetDate.setHours(0, 0, 0, 0);
          if (sDateType === 'monthYearDate') {
            dTargetDate.setDate(1);
          }

          /* Check to see if there are any offset values */
          if (/-?\d+[dmy]/i.test(sDateOffset)) {
            /* Determine the new year, month, and day values, based on any associated offsets */
            const sYearOffset = /-?\d+[y]/i.test(sDateOffset) ? sDateOffset.match(/-?\d+(?=y)/i)[0] : '0';
            const sMonthOffset = /-?\d+[m]/i.test(sDateOffset) ? sDateOffset.match(/-?\d+(?=m)/i)[0] : '0';
            const sDayOffset = /-?\d+[d]/i.test(sDateOffset) ? sDateOffset.match(/-?\d+(?=d)/i)[0] : '0';

            /* Update the Date object with the updated year, month, and day values */
            dTargetDate.setFullYear(dTargetDate.getFullYear() + parseInt(sYearOffset, 10), dTargetDate.getMonth() + parseInt(sMonthOffset, 10), dTargetDate.getDate() + parseInt(sDayOffset, 10));
          }

          /* Return the Date object after any updates have been made */
          return dTargetDate;
        },
        /* "helper.isInDateRange" method
         * "isInDateRange" checks a date input to see if it falls within a date range.  The range
         * is determined by a target date and a "before or after" indicator.  The offset uses the
         * Datepicker convention, where the day, month, and year offsets are represented by "d",
         * "m", and "y" in, each preceded by a positive or negative increment value (e.g., "7d" =
         * 7 days after the current date, "-1m" = one month prior to the current date, "-1d1y" =
         * one day less than a year after the current date, etc.)
         *
         * Parameters:
         *    sDateInputVal   - a string value that represents the date value that is being checked
         *    sDateType       - an string containing a value to identify the date format that the
         *                      field value must match.  Currently, the valid options are:
         *
         *                      - "fullDate" - value must be a valid date in the following format:
         *                          mm/dd/yyyy
         *                      - "monthYearDate" - value must be a valid date in the following
         *                        format:
         *                          mm/yyyy
         *
         *    sDateOffset     - a Datepicker formatted offset value
         *    sRangeDirection - a string value indicating whether the date input should fall
         *                      "before" or "after" the target date (derived from the "sDateOffset"
         *                      value)
         *
         * Returns:
         *    bIsDateInRange - a Boolean value indicating whether or not the date input falls
         *                     within the indicated date range.  Regardless of the
         *                     "sRangeDirection" value, the value will be "true" if the date input
         *                     is equal to the target date.
         */
        isInDateRange(sDateInputVal, sDateType, sDateOffset, sRangeDirection) {
          let bIsDateInRange = true;

          /* Get the target date for the comparison */
          const dTargetDate = validator.fn.helper.getOffsetDate(sDateOffset, sDateType);

          /* Create a date from the entered date for the comparison */
          const dEnteredDate = new Date(sDateInputVal);
          if (sRangeDirection === 'before') {
            /* The date value is "in range" if it is on or before the target date */
            bIsDateInRange = dEnteredDate <= dTargetDate;
          } else if (sRangeDirection === 'after') {
            /* The date value is "in range" if it is on or after the target date */
            bIsDateInRange = dEnteredDate >= dTargetDate;
          } else {
            /* The date value is "in range" if it equals the target date */
            bIsDateInRange = dEnteredDate === dTargetDate;
          }

          /* Return true if the date entered value is within the indicated range */
          return bIsDateInRange;
        },
        /* "helper.formatNumberForProcessing" method
         * "formatNumberForProcessing" takes a "numericValue" type value that has been formatted
         * for display and strips out any characters other than digits, the decimal separator
         * ("."), and the negative value indicator ("-"), so that it is a valid number value for
         * the back-end submission and fron-end validations.
         * Examples:
         *      - "-12,345.25"  becomes  "-12345.25"
         *
         * Parameters:
         *    sAmount            - a string value that a formatted currency value
         *
         * Returns:
         *    sUnformattedAmount - a string value of the currency amount, using only digits, a
         *                         decimal point ("."), and a negative value indicator, if present
         *                         (e.g., "-12345.67")
         */
        formatNumberForProcessing(sAmount) {
          /* Set default value of "0" for null, undefined, or empty values */
          let sUnformattedAmount = '0';

          /* Remove formatting from the value, if it is not null, undefined, or empty */
          if (validator.fn.helper.isValidStringValue(sAmount)) {
            sUnformattedAmount = sAmount.replace(/[^\d.-]/g, '');
          }
          return sUnformattedAmount;
        },
        /* "helper.formatNumberForDisplay" method
         * "formatNumberForDisplay" takes a "numericValue" type value and formats it into the
         * standard display format (i.e., adding comma separators and appending decimal values, as
         * needed).  Examples:
         *      - "123"    becomes    "123.00"
         *      - ".12"    becomes    "0.120"
         *      - "12345"  becomes    "12,345"
         *
         * Parameters:
         *    sAmount -             a string value that contains an unformatted currency value
         *    iDecimalPlaces -      an integer value that contains the number of decimal places
         *                          that should be displayed in the value. The integer must be
         *                          greater than or equal to 0.
         *    bAddCommaSeperators - a Boolean value that indicaties whether or not the value should
         *                          use comma seperators.  If no value is provided, the value is
         *                          assumed to be TRUE
         *
         * Returns:
         *    sFormattedAmount - a string value of the currency amount, formatted to include comma
         *                       seperators and 2 decimal places (e.g., "-12,345.00")
         */
        formatNumberForDisplay(sAmount, iDecimalPlaces = 0, bAddCommaSeperators = true) {
          /* Set default value of "0" for null, undefined, or empty values */
          let sFormattedAmount = '0';

          /* set the default values for parameters that are not passed in */
          // iDecimalPlaces = iDecimalPlaces || 0;
          // bAddCommaSeperators = (typeof bAddCommaSeperators === 'undefined') ? true :
          // bAddCommaSeperators;

          /* Format the value, if it is not null, undefined, or empty */
          if (validator.fn.helper.isValidStringValue(sAmount)) {
            sFormattedAmount = validator.fn.helper.formatCurrencyForSubmisson(sAmount);
            sFormattedAmount = parseFloat(sFormattedAmount).toFixed(iDecimalPlaces);
            if (bAddCommaSeperators) {
              const regexCommaDelimiterPattern = /^(-)?([^.,-]+)(\d{3}[.,])/;
              while (regexCommaDelimiterPattern.test(sFormattedAmount)) {
                sFormattedAmount = sFormattedAmount.replace(regexCommaDelimiterPattern, '$1$2,$3');
              }
            }
          }
          return sFormattedAmount;
        },
        /* "helper.formatCurrencyForSubmisson" method
         * "formatCurrencyForSubmisson" is included for backwards compatability . . . please use
         * the "formatNumberForProcessing" method going forward
         *
         * Parameters:
         *    sAmount - a string value that a formatted currency value
         *
         * Returns: a string value of the currency amount, using only digits and a period (".") as
         *          a decimal separator (e.g., "12345.67")
         */
        formatCurrencyForSubmisson(sAmount) {
          return validator.fn.helper.formatNumberForProcessing(sAmount);
        },
        /* "helper.formatCurrencyForDisplay" method
         * "formatCurrencyForDisplay" is included for backwards compatability . . . please use the
         * "formatNumberForDisplay" method going forward.
         *
         * Parameters:
         *    sAmount - a string value that an unformatted currency value
         *
         * Returns: a string value of the currency amount, formatted to include comma seperators
         *          and 2 decimal places (e.g., "-12,345.00")
         */
        formatCurrencyForDisplay(sAmount) {
          return validator.fn.helper.formatNumberForDisplay(sAmount, 2, true);
        },
        /* "helper.getDefinedParams" method
         * "getDefinedParams" takes the ruleParams array/object and an default options object,
         * determines if the ruleParams is an array or an object and sets the values to the
         * appropriate keys in the options object which gets returned back to the validation.
         *
         * Parameters:
         *    ruleParams      - an array/object holding any rule parameters
         *    options         - an object that holds the default parameters
         *
         * Returns: an object containing the user defined parameters to the validation rule calling
         * this helper function.
         */
        getDefinedParams(ruleParams, options) {
          const parameters = options;
          if (ruleParams && ruleParams instanceof Array) {
            for (let index = 0; index < ruleParams.length; index += 1) {
              const key = Object.keys(parameters)[index];
              parameters[key] = typeof ruleParams[index] !== 'undefined' ? ruleParams[index] : parameters[key];
            }
          } else if (ruleParams && ruleParams instanceof Object) {
            Object.keys(ruleParams).forEach(key => {
              parameters[key] = typeof ruleParams[key] !== 'undefined' ? ruleParams[key] : parameters[key];
            });
          }
          return parameters;
        }
      },
      /* "fn.validate" methods - contains all of the validation rules that are supported by the BOA
       * Validator framework.  Each method name matches a validation rule that can be passed in for
       * an input
       */
      validate: {
        /* "validate.alphaOnly" method
         * "alphaOnly" validates that an input value contains only letters.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters (currently not used)
         *
         * Returns:
         *    true  - if the input value contains only letters
         *    false - if the input value contains any non-letter characters
         */
        alphaOnly($element, ruleParams) {
          const regexAlphaOnlyPattern = /^[a-z]+$/i;
          return validator.fn.helper.matchesPattern($element, regexAlphaOnlyPattern);
        },
        /* "validate.numberOnly" method
         * "numberOnly" validates that an input value contains only numbers.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters (currently not used)
         *
         * Returns:
         *    true  - if the input value contains only numbers
         *    false - if the input value contains any non-letter numbers
         */
        numberOnly($element, ruleParams) {
          const regexNumberOnlyPattern = /^\d+$/;
          return validator.fn.helper.matchesPattern($element, regexNumberOnlyPattern);
        },
        /* "validate.noNumbers" method
         * "noNumbers" validates that an input value contains any numbers.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters (currently not used)
         *
         * Returns:
         *    true  - if the input value contains any numbers
         *    false - if the input value does not contain any numbers
         */
        noNumbers($element, ruleParams) {
          const regexNotNumberPattern = /^\D+$/;
          return validator.fn.helper.matchesPattern($element, regexNotNumberPattern);
        },
        /* "validate.alphaNumeric" method
         * "alphaNumeric" validates that an input value contains only letters and numbers.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters (currently not used)
         *
         * Returns:
         *    true  - if the input value contains only letters and numbers
         *    false - if the input value contains any non-alphanumeric characters
         */
        alphaNumeric($element, ruleParams) {
          const regexAlphaNumericPattern = /^[a-z\d]+$/i;
          return validator.fn.helper.matchesPattern($element, regexAlphaNumericPattern);
        },
        /* "validate.nameCharacters" method
         * "nameCharacters" validates that an input value contains only characters that are valid
         * for name data.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters.
         *
         *                      allowSpecialChars (boolean) [0]: Optionally contain a boolean value
         *                          representing whether or not to allow special characters.
         *                          [Default: true]
         *
         * Returns:
         *    true  - if the input value contains only name characters
         *    false - if the input value contains any non-name characters
         */
        nameCharacters($element, ruleParams) {
          let options = {
            allowSpecialChars: true
          };
          let regexNameCharactersPattern = /^[a-z]([a-z '.-]+)?$/i;
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          if (options.allowSpecialChars === false) {
            regexNameCharactersPattern = /^[a-z]([a-z ]+)?$/i;
          }
          return validator.fn.helper.matchesPattern($element, regexNameCharactersPattern);
        },
        /* "validate.maxChecked" method
         * "maxChecked" validates that only the provided number of checkboxes are checked in a group.
         *
         * Parameters:
         *    $element        - a jQuery reference to the fieldset containing the group of checkboxes.
         *    ruleParams      - an array/object holding any rule parameters.
         *
         *                      maxNumber (number) [0]: Number of checkboxes allowed.
         *
         * Returns:
         *    true  - if the number of checkboxes checked is within the allowed number
         *    false - if the number of checkboxes checked is greater than the allowed number
         */
        maxChecked($element, ruleParams) {
          let options = {
            maxNumber: ''
          };
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          if (options.maxNumber) {
            if ($element.find('input').filter(':checked').length > options.maxNumber) {
              return false;
            }
          }
          return true;
        },
        /* "validate.namePrefix" method
         * "namePrefix" validates that an input value contains only characters that are valid
         * for name prefix data.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters (currently not used)
         *
         * Returns:
         *    true  - if the input value matches name prefix format
         *    false - if the input value does not match name prefix format
         */
        namePrefix($element, ruleParams) {
          const regexNamePrefixPattern = /^[A-Z]+$/;
          return validator.fn.helper.matchesPattern($element, regexNamePrefixPattern);
        },
        /* "validate.nameSuffix" method
         * "nameSuffix" validates that an input value contains only characters that are valid
         * for name suffix data.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters (currently not used)
         *
         * Returns:
         *    true  - if the input value matches name suffix format
         *    false - if the input value does not match name suffix format
         */
        nameSuffix($element, ruleParams) {
          const regexNameSuffixPattern = /^[A-Z]+$/;
          return validator.fn.helper.matchesPattern($element, regexNameSuffixPattern);
        },
        /* "validate.nameGeneration" method
         * "nameGeneration" validates that an input value contains only characters that are valid
         * for name generation data.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters (currently not used)
         *
         * Returns:
         *    true  - if the input value matches name generation format
         *    false - if the input value does not match name generation format
         */
        nameGeneration($element, ruleParams) {
          const regexNameGenerationPattern = /^[A-Z]+$/;
          return validator.fn.helper.matchesPattern($element, regexNameGenerationPattern);
        },
        /* "validate.phone" method
         * "phone" validates that an input value is formatted in the standard pattern for a US
         * phone number (e.g., 123-456-7890).
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters (currently not used)
         *
         * Returns:
         *    true  - if the input value matches the phone number pattern
         *    false - if the input value does not match the phone number pattern
         */
        phone($element, ruleParams) {
          /* Remove any non-digit characters from the value */
          const sPhoneValue = $element.val().replace(/\D/g, '');
          const regexPhonePattern = /^(\d{3})(\d{3})(\d{4})$/;

          /* Validate the value against the phone format */
          const bIsValidPhone = validator.fn.helper.matchesPattern(sPhoneValue, regexPhonePattern);

          /* If the value meets the phone requirements, apply the standard format and update the
              field value */
          if (bIsValidPhone) {
            const aPhoneElements = sPhoneValue.match(regexPhonePattern);
            $element.val(aPhoneElements.slice(1).join('-'));
          }
          return bIsValidPhone;
        },
        /* "validate.email" method
         * "email" validates that an input value is formatted in the standard pattern for an email
         * values.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters.
         *
         *                      validCharacters (string) [0]: Optionally contain a string including
         *                          valid special characters (Not including dot, period, full stop).
         *                          [Default: "_-"]
         *
         * Returns:
         *    true  - if the input value matches the email format
         *    false - if the input value does not match the email format
         */
        email($element, ruleParams) {
          let options = {
            validCharacters: '_-'
          };
          options = validator.fn.helper.getDefinedParams(ruleParams, options);

          // const regexEmailPattern = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)*$/i;

          const regexEmailPattern = new RegExp(`^[a-z0-9${options.validCharacters}](([a-z0-9.${options.validCharacters}]+)?[a-z0-9${options.validCharacters}])?@[a-z0-9.-]+([.][a-z0-9-]+)+$`, 'i');
          const maxCharParams = ['.', 1];
          const maxCharResult = validator.fn.validate.maxRepeatedChar($element, maxCharParams);
          if (!maxCharResult) {
            return {
              ruleName: 'maxRepeatedChar',
              ruleParams: maxCharParams
            };
          }
          return validator.fn.helper.matchesPattern($element, regexEmailPattern);
        },
        /* "validate.phoneOrEmail" method
         * "phoneOrEmail" validates that an input value is formatted in the standard pattern for
         * either a phone number or an email value.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters (currently not used)
         *
         * Returns:
         *    true  - if the input value matches either the phone or email formats
         *    false - if the input value does not match either the phone or email formats
         */
        phoneOrEmail($element, ruleParams) {
          return validator.fn.validate.phone($element, ruleParams) || validator.fn.validate.email($element, ruleParams);
        },
        /* "validate.ssn" method
         * "ssn" validates that an input value is formatted in the standard pattern for a Social
         * Security Number or Tax Identification Number (e.g., 123-45-6789).
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters (currently not used)
         *
         * Returns:
         *    true  - if the input value matches the SSN/TIN pattern
         *    false - if the input value does not match the SSN/TIN pattern
         */
        ssn($element, ruleParams) {
          /* Remove any non-digit characters from the value */
          const sSSNValue = $element.val().replace(/\D/g, '');
          const regexSSNPattern = /^(\d{3})(\d{2})(\d{4})$/;

          /* Validate the value against the SSN/TIN format */
          const bIsValidSSN = validator.fn.helper.matchesPattern(sSSNValue, regexSSNPattern);

          /* If the value meets the SSN/TIN requirements, apply the standard format and update the
              field value */
          if (bIsValidSSN) {
            const aSSNElements = sSSNValue.match(regexSSNPattern);
            $element.val(aSSNElements.slice(1).join('-'));
          }
          return bIsValidSSN;
        },
        /* "validate.ein" method
         * "ein" validates that an input value is formatted in the standard pattern for an Employer
         * Identification Number (e.g., 12-3456789).
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters (currently not used)
         *
         * Returns:
         *    true  - if the input value matches the EIN pattern
         *    false - if the input value does not match the EIN pattern
         */
        ein($element, ruleParams) {
          /* Remove any non-digit characters from the value */
          const sEINValue = $element.val().replace(/\D/g, '');
          const regexEINPattern = /^(\d{2})(\d{7})$/;

          /* Validate the value against the EIN format */
          const bIsValidEIN = validator.fn.helper.matchesPattern(sEINValue, regexEINPattern);

          /* If the value meets the EIN requirements, apply the standard format and update the
              field value */
          if (bIsValidEIN) {
            const aEINElements = sEINValue.match(regexEINPattern);
            $element.val(aEINElements.slice(1).join('-'));
          }
          return bIsValidEIN;
        },
        /* "validate.idCharacters" method
         * "idCharacters" validates that an input value contains only characters that are valid for
         * an identification number.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters (currently not used)
         *
         * Returns:
         *    true  - if the input value contains only identification number characters
         *    false - if the input value contains any non-identification number characters
         */
        idCharacters($element, ruleParams) {
          const regexIdCharactersPattern = /^[a-z0-9][a-z0-9*]*$/i;
          return validator.fn.helper.matchesPattern($element, regexIdCharactersPattern);
        },
        /* "validate.streetAddress" method
         * "streetAddress" validates that an input value is formatted in the standard pattern for
         * street address values.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters.
         *
         *                      validCharacters (string) [0]: Optionally contain a string including
         *                          valid special characters.  [Default: "#/.-"]
         *
         *                      isMailingAddress (boolean) [1]: The second parameter is a boolean
         *                          value indicating if the address is a mailing address.
         *                          [Default: true]
         *
         * Returns:
         *    true  - if the input value matches the street address format
         *    false - if the input value does not match the street address format
         */
        streetAddress($element, ruleParams) {
          let options = {
            validCharacters: '#/.-',
            isMailingAddress: true
          };
          const invalidAddressPatterns = [/P\s*\.?\s*O\s*\.?\s*BOX/i];
          let invalidAddressResult = false;
          let regexStreetAddressPattern;
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          if (options.isMailingAddress === false) {
            regexStreetAddressPattern = new RegExp(`^[\\d][a-z\\d ${options.validCharacters}]*$`, 'i');
            invalidAddressPatterns.every(invalidAddressPattern => {
              invalidAddressResult = validator.fn.helper.matchesPattern($element, invalidAddressPattern);
              if (invalidAddressResult) {
                return false;
              }
              return true;
            });
          } else {
            regexStreetAddressPattern = new RegExp(`^[a-z\\d][a-z\\d ${options.validCharacters}]*$`, 'i');
          }
          if (invalidAddressResult) {
            return {
              ruleName: 'mailingAddress'
            };
          }
          return validator.fn.helper.matchesPattern($element, regexStreetAddressPattern);
        },
        /* "validate.intlStreetAddress" method
         * "intlStreetAddress" validates that an input value is formatted in the standard pattern
         * for international street address values.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters.
         *
         *                      validCharacters (string) [0]: Optionally contain a string including
         *                          valid special characters.  [Default: "#/.,'-"]
         *
         *                      isMailingAddress (boolean) [1]: The second parameter is a boolean
         *                          value indicating if the address is a mailing address.
         *                          [Default: true]
         *
         * Returns:
         *    true  - if the input value matches the international street address format
         *    false - if the input value does not match the international street address format
         */
        intlStreetAddress($element, ruleParams) {
          let options = {
            validCharacters: "#/.,'-",
            isMailingAddress: true
          };
          const invalidAddressPatterns = [/P\s*\.?\s*O\s*\.?\s*BOX/i];
          let invalidAddressResult = false;
          let regexIntlStreetAddressPattern;
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          if (options.isMailingAddress === false) {
            regexIntlStreetAddressPattern = new RegExp(`^[\\d][a-z\\d ${options.validCharacters}]*$`, 'i');
            invalidAddressPatterns.every(invalidAddressPattern => {
              invalidAddressResult = validator.fn.helper.matchesPattern($element, invalidAddressPattern);
              if (invalidAddressResult) {
                return false;
              }
              return true;
            });
          } else {
            regexIntlStreetAddressPattern = new RegExp(`^[a-z\\d][a-z\\d ${options.validCharacters}]*$`, 'i');
          }
          if (invalidAddressResult) {
            return {
              ruleName: 'mailingAddress'
            };
          }
          return validator.fn.helper.matchesPattern($element, regexIntlStreetAddressPattern);
        },
        /* "validate.unitNumber" method
         * "unitNumber" validates that an input value is formatted for address unit number values.
         * (e.g. Apt. #, Suite #)
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters.
         *
         *                      validCharacters (string) [0]: Optionally contain a string including
         *                          valid special characters.  [Default: "#.'-"]
         *
         * Returns:
         *    true  - if the input value matches the international street address format
         *    false - if the input value does not match the international street address format
         */
        unitNumber($element, ruleParams) {
          let options = {
            validCharacters: "#.'-"
          };
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          const regexUnitNumberPattern = new RegExp(`^[a-z\\d][a-z\\d ${options.validCharacters}]*$`, 'i');
          return validator.fn.helper.matchesPattern($element, regexUnitNumberPattern);
        },
        /* "validate.cityCounty" method
         * "cityCounty" validates that an input value is formatted in the standard pattern for a
         * city or county name values.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters.
         *
         *                      region (string) [0]: Optionally contain one of three string values
         *                          for the country/international ("US", "CANADA", "INTERNATIONAL").
         *                          [Default: "US"]
         *
         *                      validCharacters (string) [1]: a string including valid special
         *                          characters.  Note: The dash must be at the end of the
         *                          string or escaped to avoid issues.  Also, a custom error message
         *                          should be provided since the default will be incorrect if
         *                          additional characters are provided. [Default: "-"]
         *
         * Returns:
         *    true  - if the input value matches the city/county format
         *    false - if the input value does not match the city/county format
         */
        cityCounty($element, ruleParams) {
          let options = {
            region: 'US',
            validCharacters: '-'
          };
          if (options.region === 'CANADA') {
            options.validCharacters = ",.'-";
          }
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          const regexCityCountyPattern = new RegExp(`^[a-z][a-z ${options.validCharacters}]*$`, 'i');
          let bValidResult = validator.fn.helper.matchesPattern($element, regexCityCountyPattern);
          if (!bValidResult && options.region === 'CANADA') {
            bValidResult = {
              ruleName: 'cityCanada'
            };
          }
          return bValidResult;
        },
        /* "validate.stateCode" method
         * "stateCode" validates that an input value is formatted in the standard pattern for a
         * state or province code values.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters.
         *
         *                      region (string) [0]: Optionally contain one of three string values
         *                          for the country/international ("US", "CANADA", "INTERNATIONAL").
         *                          [Default: "US"]
         *
         * Returns:
         *    true  - if the input value matches the state/province code format
         *    false - if the input value does not match the state/province format
         */
        stateCode($element, ruleParams) {
          let options = {
            region: 'US'
          };
          const regexStateCodePattern = /^[a-z]+$/i;
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          return validator.fn.helper.matchesPattern($element, regexStateCodePattern);
        },
        /* "validate.zip" method
         * "zip" validates that an input value is formatted in the standard pattern for a US zip
         * code (e.g., 12345 or 12345-6789).
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters (currently not used)
         *
         * Returns:
         *    true  - if the input value matches the zip code pattern
         *    false - if the input value does not match the zip code pattern
         */
        zip($element, ruleParams) {
          /* Remove any non-digit characters from the value */
          let sZipCodeValue = $element.val().replace(/\D/g, '');
          const regexZipCodePattern = /^(\d{5})(\d{4})?$/;

          /* Validate the value against the zip code format */
          const bIsValidZipCode = validator.fn.helper.matchesPattern(sZipCodeValue, regexZipCodePattern);

          /* If the value meets the zip code requirements, apply the standard format and update the
              field value */
          if (bIsValidZipCode) {
            const aZipCodeElements = sZipCodeValue.match(regexZipCodePattern);
            if (aZipCodeElements[2] !== undefined) {
              sZipCodeValue = aZipCodeElements.slice(1).join('-');
            }
            $element.val(sZipCodeValue);
          }
          return bIsValidZipCode;
        },
        /* "validate.countryCode" method
         * "countryCode" validates that an input value is formatted in the standard pattern
         * for ISO-2 or ISO-3 country code values.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters.
         *
         *                      isoFormat (string) [0]: Optionally contain a string value
         *                          representing the format of the country code (e.g. "ISO-2",
         *                          "ISO-3", "ISO-3166") [Default: "ISO-2"]
         *
         * Returns:
         *    true  - if the input value matches the country code format
         *    false - if the input value does not match the country code format
         */
        countryCode($element, ruleParams) {
          let options = {
            isoFormat: 'ISO-2'
          };
          let regexCountryCodePattern = /^[a-z]+$/i;
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          if (options.isoFormat === 'ISO-3166') {
            regexCountryCodePattern = /^[1-9][0-9]*$/;
          }
          return validator.fn.helper.matchesPattern($element, regexCountryCodePattern);
        },
        /* "validate.numericValue" method
         * "numericValue" validates that an input value is numeric value, incluiding an optional
         * number of decimal places, determined by a parameter.  The value will have 0 added to the
         * end of the value, to meet the indicated number of decimal places (e.g., "12.3" will be
         * updated to "12.30") and a 0 will be prepended if there is no number entered before the
         * decimal point (e.g., ".12" will be updated to "0.12").
         *
         * Additionally, commas seprators will be added to seperate houndreds, thousands, millions,
         * etc., by default but can be overridden in the rule parameters.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding the required rule parameters.
         *
         *                      decimalPlaces (integer) [0]: The first parameter must contain an
         *                          integer, indicating the number of decimal places that should be
         *                          displayed in the value.  The integer must be greater than or
         *                          equal to 0.  [Default: 0]
         *
         *                      addCommaSeperators (boolean) [1]:  The second parameter must contain
         *                          a Boolean value indicating whether or not the value should use
         *                          comma seperators.  [Default: true]
         *
         *    formatNumberInDisplay - a boolean check if the value in the input field needs to be
         *                          updated to fit the format requirements (i.e., with comma
         *                          separators).
         *
         * Returns:
         *    true  - if the input value is a valid numeric value, with the correct number of
         *            decimal places
         *    false - if the input value is not a valid numeric value, with the correct number of
         *            decimal places
         */
        numericValue($element, ruleParams, formatNumberInDisplay = true) {
          /* Determine the values for decimal places an comma seperator support, based on the rule
           * parameters */
          let options = {
            decimalPlaces: 0,
            addCommaSeperators: true
          };
          options = validator.fn.helper.getDefinedParams(ruleParams, options);

          /* Determine which patterns are needed to validate the numeric value, based on the decimal
           * places and comma separators */
          const sRegexIntegerString = '(\\d{1,3}(,\\d{3})*|\\d+)';
          const sRegexDecimalString = options.decimalPlaces > 0 ? `(\\.\\d{0,${options.decimalPlaces}})?|\\.\\d{1,${options.decimalPlaces}}` : '';

          /* Create the final pattern, including support for a negative value indicator ("-") */
          const regexCurrencyPattern = new RegExp(`^-?(${sRegexIntegerString}${sRegexDecimalString})$`);

          /* Validate that the input matches the derived number pattern */
          const isValidCurrency = validator.fn.helper.matchesPattern($element, regexCurrencyPattern);

          /* If the input is a valid dollar amount, force the value to match the pattern by adding
           * the correct formatting, as necessary */
          if (isValidCurrency && formatNumberInDisplay) {
            let sElementVal = $element.val();
            sElementVal = validator.fn.helper.formatNumberForDisplay(sElementVal, options.decimalPlaces, options.addCommaSeperators);
            $element.val(sElementVal);
          }
          return isValidCurrency;
        },
        /* "validate.integer" method
         * "integer" validates that an input value is a numeric value with no decimal places.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters.
         *
         *                      addCommaSeperators (boolean) [0]: indicates if comma seperators
         *                          should be added to the value, to seperate hundreds, thousands,
         *                          millions, etc.
         *
         * Returns:
         *    true  - if the input value matches the integer pattern
         *    false - if the input value does not match the integer pattern
         */
        integer($element, ruleParams) {
          let options = {
            addCommaSeperators: false
          };
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          return validator.fn.validate.numericValue($element, {
            decimalPlaces: 0,
            addCommaSeperators: options.addCommaSeperators
          });
        },
        /* "validate.currency" method
         * "currency" validates that an input value is formatted in the standard pattern for a US
         * dollar amounts.  If the value is a valid dollar amount, but does not match the dollar
         * format, the validation will append or prepend zeros to make the value match the pattern
         * (e.g., "12.3" will be updated to "12.30", ".12" will be updated to "0.12", etc.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters
         *
         *                      digitsOptional (boolean) [0]: optional digits [Default: true]
         *                      allowNegative (boolean) [1]: allow negative sign [Default: false]
         *
         * Returns:
         *    true  - if the input value matches the currency pattern
         *    false - if the input value does not match the currency pattern
         */
        currency($element, ruleParams) {
          let options = {
            digitsOptional: true,
            allowNegative: false
          };
          const enteredValue = $element.val();
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          let regexCurrencyFormat;
          if (options.digitsOptional) {
            if (options.allowNegative) {
              regexCurrencyFormat = /^[-$]*?[\d,]+\.?(\d{2})?$/;
            } else {
              regexCurrencyFormat = /^\$?[\d,]+\.?(\d{2})?$/;
            }
          } else if (options.allowNegative) {
            regexCurrencyFormat = /^[-$]*?[\d,]+?$/;
          } else {
            regexCurrencyFormat = /^\$?[\d,]+?$/;
          }
          if (regexCurrencyFormat.test(enteredValue)) {
            return true;
          }
          return false;
        },
        /* "validate.isWeekday" method
         *
         * Parameters:
         *    $dateElement   - a jQuery reference to the input element whose value is being checked
         *    ruleParams     - an array/object holding any rule parameters.
         *
         * Returns:
         *    true  - if the input value is found to be a weekday
         *    false - if the input value is found to be a weekend
         */
        isWeekday($dateElement, ruleParams) {
          const options = validator.fn.helper.getDefinedParams(ruleParams, {
            dateType: 'fullDate'
          });
          if (options.dateType !== 'fullDate') {
            throw TypeError('validate.isWeekday can only be run with a fullDate, mm/dd/yyyy');
          }
          const blnValidDate = validator.fn.validate.dateFormat($dateElement, options);
          if (!blnValidDate) {
            return false;
          }
          const sDateInputVal = $.trim($dateElement.val()); // Input value
          const sDateTimestamp = Date.parse(sDateInputVal); // Date timestamp
          const sDateDate = new Date(sDateTimestamp); // Date Object
          const dow = sDateDate.getDay(); // Day of Week
          if (dow === 0 || dow === 6) {
            // 0: Sunday; 6: Saturday
            return false;
          }
          // All other values, weekday
          return true;
        },
        /* "validate.dateFormat" method
         * "dateFormat" validates that an input value is formatted in the standard pattern for a US
         * date pattern (e.g., 01/01/1970).
         *
         * Parameters:
         *    $dateElement   - a jQuery reference to the input element whose value is being checked
         *    ruleParams     - an array/object holding any rule parameters.
         *
         *                     dateType (string) [0]: This must contain a value to identify the date
         *                        format that the field value must match. Currently, the valid
         *                        options are:
         *
         *                          - "fullDate" - value must be a valid date in the following
         *                            format: mm/dd/yyyy
         *                          - "monthYearDate" - value must be a valid date in the following
         *                            format: mm/yyyy
         *
         * Returns:
         *    true  - if the input value matches the date pattern
         *    false - if the input value does not match the date pattern
         */
        dateFormat($dateElement, ruleParams) {
          let options = {
            dateType: null
          };
          let bIsValidDate;
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          const sDateInputVal = $.trim($dateElement.val());
          /* Set the pattern to match the "mm/dd/yyyy" date format */
          let regexDateFormatPattern = /^(\d{1,2})(\D+)(\d{1,2})(\D+)(\d{4})$/;

          /* If the date format is "mm/yyyy", update the default validation values */
          if (options.dateType === 'monthYearDate') {
            /* Set the pattern to match the "mm/yyyy" date format */
            regexDateFormatPattern = /^(\d{1,2})(\D+)(\d{4})$/;
          }

          /* Validate the value against the date format */
          const bIsValidDateFormat = validator.fn.helper.matchesPattern(sDateInputVal, regexDateFormatPattern);

          /* If the value meets the date requirements, apply the standard format and update the
              field value */
          if (bIsValidDateFormat) {
            const aDateInputVals = sDateInputVal.match(regexDateFormatPattern);
            let sFormattedDisplayDate = '';
            let sFormattedCompareDate = '';

            /* If the date contains only a month and year, set the day to the first day of the month
             * for comparison and apply the standard date formats */
            if (options.dateType === 'monthYearDate') {
              sFormattedDisplayDate = validator.fn.helper.formatDate([validator.fn.helper.formatMonthDay(aDateInputVals[1]), aDateInputVals[3]], 'monthYearDate');
              sFormattedCompareDate = validator.fn.helper.formatDate(validator.fn.helper.convertMonthYearToFullDate([aDateInputVals[1], aDateInputVals[3]], '01'), 'fullDate');
            } else {
              /* If the date is a standard month/day/year date, apply the standard date format */
              sFormattedDisplayDate = validator.fn.helper.formatDate([aDateInputVals[1], aDateInputVals[3], aDateInputVals[5]], options.dateType);
              sFormattedCompareDate = sFormattedDisplayDate;
            }

            /* Check to see if the value is a valid date */
            bIsValidDate = validator.fn.helper.isValidDate(sFormattedCompareDate);

            /* If the value is a valid date, apply the standard format and update the
            field value */
            if (bIsValidDate) {
              $dateElement.val(sFormattedDisplayDate);
            }
          } else {
            return bIsValidDateFormat;
          }
          return bIsValidDate;
        },
        /* "validate.startDate" method
         * "startDate" validates a date input to see if it falls on or after a minimum date.
         *
         * Parameters:
         *    $dateElement     - a jQuery reference to the date input element whose value is being
         *                       checked
         *    ruleParams       - an array/object holding any rule parameters.
         *
         *                       dateOffset (string) [0]: Must contain an offset string, used to
         *                          determine the minimum date. The offset uses the the Datepicker
         *                          convention, where the day, month, and year offsets are
         *                          represented by "d", "m", and "y" in, each preceded by a positive
         *                          or negative increment value (e.g., "7d" = 7 days after the
         *                          current date, "-1m" = one month prior to the current date,
         *                          "-1d1y" = one day less than a year after the current date, etc.)
         *
         *                       dateType (string) [1]: Must contain date type string (either
         *                          "fullDate" ["mm/dd/yyyy"] or "monthYearDate" ["mm/yyyy"]).
         *
         *
         * Returns:
         *    true  - if the date value is on or after the minimum date
         *    false - if the date value is before the minimum date
         */
        startDate($dateElement, ruleParams) {
          let options = {
            dateOffset: null,
            dateType: null
          };
          let sDateVal = $dateElement.val();
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          if (options.dateType === 'monthYearDate') {
            sDateVal = validator.fn.helper.convertMonthYearToFullDate(sDateVal, '01');
          }
          return validator.fn.validate.dateFormat($dateElement, options) && validator.fn.helper.isInDateRange(sDateVal, options.dateType, options.dateOffset, 'after');
        },
        /* "validate.endDate" method
         * "endDate" validates a date input to see if it falls on or before a maximum date.
         *
         * Parameters:
         *    $dateElement     - a jQuery reference to the date input element whose value is being
         *                       checked
         *    ruleParams       - an array/object holding any rule parameters.
         *
         *                       dateOffset (string) [0]: Must contain an offset string, used to
         *                          determine the maximum date. The offset uses the the Datepicker
         *                          convention, where the day, month, and year offsets are
         *                          represented by "d", "m", and "y" in, each preceded by a positive
         *                          or negative increment value (e.g., "7d" = 7 days after the
         *                          current date, "-1m" = one month prior to the current date,
         *                          "-1d1y" = one day less than a year after the current date, etc.)
         *
         *                       dateType (string) [1]: Date type string (either "fullDate"
         *                          ["mm/dd/yyyy"] or "monthYearDate" ["mm/yyyy"]).
         *
         * Returns:
         *    true  - if the date value is on or before the maximum date
         *    false - if the date value is after the maximum date
         */
        endDate($dateElement, ruleParams) {
          let options = {
            dateOffset: null,
            dateType: null
          };
          let sDateVal = $dateElement.val();
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          if (options.dateType === 'monthYearDate') {
            sDateVal = validator.fn.helper.convertMonthYearToFullDate(sDateVal, '01');
          }
          return validator.fn.validate.dateFormat($dateElement, options) && validator.fn.helper.isInDateRange(sDateVal, options.dateType, options.dateOffset, 'before');
        },
        /* "validate.dateRange" method
         * "dateRange" validates a date input to see if it falls on or before a maximum date.
         *
         * Parameters:
         *    $dateElement      - a jQuery reference to the date input element whose value is being
         *                        checked
         *    ruleParams        - an array/object holding any rule parameters.
         *
         *                        minDateOffset (string) [0]: Must contain an offset string, used to
         *                            determine the minimum date. The offset uses the the Datepicker
         *                            convention, where the day, month, and year offsets are
         *                            represented by "d", "m", and "y" in, each preceded by a
         *                            positive or negative increment value (e.g., "7d" = 7 days
         *                            after the current date, "-1m" = one month prior to the
         *                            current date, "-1d1y" = one day less than a year after the
         *                            current date, etc.)
         *
         *                        maxDateOffset (string) [1]: Must contain an offset string, used to
         *                            determine the maximum date. The offset uses the the Datepicker
         *                            convention, where the day, month, and year offsets are
         *                            represented by "d", "m", and "y" in, each preceded by a
         *                            positive or negative increment value (e.g., "7d" = 7 days
         *                            after the current date, "-1m" = one month prior to the
         *                            current date, "-1d1y" = one day less than a year after the
         *                            current date, etc.)
         *
         *                        dateType (string) [2]: Must contain a date type string (either
         *                            "fullDate" ["mm/dd/yyyy"] or "monthYearDate" ["mm/yyyy"]).
         *
         * Returns:
         *    true  - if the date value is within the minimum and maximum date range
         *    false - if the date value is before or after the minimum and maximum date range
         */
        dateRange($dateElement, ruleParams) {
          let options = {
            minDateOffset: null,
            maxDateOffset: null,
            dateType: null
          };
          options = validator.fn.helper.getDefinedParams(ruleParams, options);

          /* Check to see if the date is within both ends of the date range */
          return validator.fn.validate.startDate($dateElement, [options.minDateOffset, options.dateType]) && validator.fn.validate.endDate($dateElement, [options.maxDateOffset, options.dateType]);
        },
        /* "validate.minLength" method
         * "minLength" validates that the length of an input value is greater than or equal to a
         * minimum length.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters.
         *
         *                      minLength (integer) [0]: Must contain an integer, indicating the
         *                          minimum length.  The integer must be 0 or greater.
         *
         * Returns:
         *    true  - if the length of the input value is greater than or equal to the minimum
         *            length
         *    false - if the length of the input value is less than the minimum length
         */
        minLength($element, ruleParams) {
          let options = {
            minLength: null
          };
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          return $element.val().length >= options.minLength;
        },
        /* "validate.maxLength" method
         * "maxLength" validates that the length of an input value is less than or equal to a
         * maximum length.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters.
         *
         *                      maxLength (integer) [0]: Must contain an integer, indicating the
         *                          maximum length.  The integer must be greater than 0.
         *
         * Returns:
         *    true  - if the length of the input value is less than or equal to the maximum length
         *    false - if the length of the input value is greater than the maximum length
         */
        maxLength($element, ruleParams) {
          let options = {
            maxLength: null
          };
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          return $element.val().length <= options.maxLength;
        },
        /* "validate.lengthRange" method
         * "lengthRange" validates that the length of an input value value falls between a minimum
         * and maximum length.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters.
         *
         *                      minLength (integer) [0]: Must contain an integer, indicating the
         *                          minimum length.  The integer must be 0 or greater.
         *
         *                      maxLength (integer) [1]: Must contain an integer, indicating the
         *                          maximum length.  The second integer is optional, but must be
         *                          greater than the first value, when present.
         *
         * Returns:
         *    true  - if the length of the input value is within the minimum and maximum length
         *            range
         *    false - if the length of the input value is greater or less than the minimum and
         *            maximum length range
         */
        lengthRange($element, ruleParams) {
          let options = {
            minLength: null,
            maxLength: null
          };
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          return validator.fn.validate.minLength($element, [options.minLength]) && validator.fn.validate.maxLength($element, [options.maxLength]);
        },
        /* "validate.minValue" method
         * "minValue" validates that the value of an input is greater than or equal to than a
         * minimum number.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters.
         *
         *                      minValue (Number) [0]: Must contain an number indicating the minimum
         *                          value allowed for the field.
         *
         * Returns:
         *    true  - if the value of the input value is greater than or equal to the minimum value
         *    false - if the value of the input value is less than the minimum value
         */
        minValue($element, ruleParams) {
          let options = {
            minValue: null
          };
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          return Number($element.val().replace(/[^\d.-]/g, '')) >= options.minValue;
        },
        /* "validate.maxValue" method
         * "maxValue" validates that the value of an input is less than or equal to than a maximum
         *  number.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters.
         *
         *                      maxValue (Number) [0]: Must contain an number indicating the maximum
         *                          value allowed for the field.
         *
         * Returns:
         *    true  - if the value of the input value is less than or equal to the maximum value
         *    false - if the value of the input value is greater than the maximum value
         */
        maxValue($element, ruleParams) {
          let options = {
            maxValue: null
          };
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          return Number($element.val().replace(/[^\d.-]/g, '')) <= options.maxValue;
        },
        /* "validate.valueRange" method
         * "valueRange" validates that the value of an input falls between a minimum and
         * maximum number.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters.
         *
         *                      minValue (Number) [0]: Must contain an number indicating the minimum
         *                          value allowed for the field.
         *
         *                      maxValue (Number) [1]: Must contain an number indicating the maximum
         *                          value allowed for the field. This number must be greater than
         *                          the first number.
         *
         * Returns:
         *    true  - if the value of the input is within the minimum and maximum value range
         *    false - if the value of the input is greater or less than the minimum and maximum
         *            value range
         */
        valueRange($element, ruleParams) {
          let options = {
            minValue: null,
            maxValue: null
          };
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          return validator.fn.validate.minValue($element, [options.minValue]) && validator.fn.validate.maxValue($element, [options.maxValue]);
        },
        /* "validate.multipleOf" method
         * "multipleOf" validates that the value of an input  is a multiple of one or more,
         * parameter-driven numbers.  The value must be a multiple of each number provided in the
         * parameters in order to pass the validation.
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters.
         *
         *                      multipliers (Array): Must contain at least one number.  Each number
         *                          present in the array will be tested individually to see if it is
         *                          a factor of the value contained in the field.
         *
         * Returns:
         *    true  - if the value of the input value is a multiple of all of the parameter numbers
         *    false - if the value of the input value is not a multiple of any one of the parameter
         *            numbers
         */
        multipleOf($element, ruleParams) {
          const options = {
            multipliers: null
          };
          let isMultiple = true;
          if (ruleParams && ruleParams instanceof Array) {
            options.multipliers = ruleParams;
          } else {
            options.multipliers = typeof ruleParams.multipliers !== 'undefined' ? ruleParams.multipliers : options.multipliers;
          }
          for (let i = 0; i < options.multipliers.length; i += 1) {
            /* Due to issues with JavaScript's storage of floating point values, we need check that
             * the input value a multiple of the parameter using a a tolerance, rather than simply
             * checking that there is no remainder at all */
            if (Math.abs(Number($element.val().replace(/,/g, '')) * 100 % (options.multipliers[i] * 100)) > 0.0001) {
              isMultiple = false;
              break;
            }
          }
          return isMultiple;
        },
        /* "validate.maxRepeatedChar" method
         * "maxRepeatedChar" validates that the value of an input does not contain any characters,
         * from a group passed in as a parameter, that are repeated more the a set number of times
         * (also passed in as a parameter).
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding the required rule parameters.
         *
         *                      characterSet (string) [0]: The first parameter must contain a
         *                          string value that is a valid, case-insensitive, regular
         *                          expression group (e.g., "a-zA-Z0-9")
         *
         *                          NOTE: if a double quote or a right square bracket need to be
         *                              included in the character group, the double quote MUST be
         *                              preceded by a single backslash (e.g.:  \"  ) and the right
         *                              square bracket MUST be preceded by a double backslash
         *                              (e.g.:  \\]  ), in order to avoid JavaScript errors.
         *
         *                       maxRepeated (integer) [1]: The second parameter must contain a
         *                          number value that represents the maximum number of times a
         *                          character can repeat before the value is invalid
         *
         *                       caseInsensitive (boolean) [2]: The third parameter is a Boolean
         *                          value that specifies whether the RegEx should be case
         *                          insensitive.
         *
         * Returns:
         *    true  - if the none of the characters repeats more than the maximum number of times
         *            in the value
         *    false - if at least one of the characters repeats more than the maximum number of
         *            times in the value
         */
        maxRepeatedChar($element, ruleParams) {
          let options = {
            characterSet: null,
            maxRepeated: null,
            caseInsensitive: false
          };
          let bRepeatsAreUnderMax = true;
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          try {
            let flags = '';
            if (options.caseInsensitive) {
              flags = 'i';
            }
            const regexMaxRepeatedPattern = new RegExp(`([${options.characterSet}])\\1{${options.maxRepeated}}`, flags);
            bRepeatsAreUnderMax = !validator.fn.helper.matchesPattern($element, regexMaxRepeatedPattern);
          } catch (e) {
            console.log(`Validation Error - rule: 'maxRepeatedChar'\n
              '$element' = ${$element}, ruleParams = ${ruleParams}\n
              Error message: ${e.message}`);
          }
          return bRepeatsAreUnderMax;
        },
        /* "validate.isNotSingleRepeatingChar" method
         * "isNotSingleRepeatingChar" validates that the value of an input field is not only made up
         * of a single repeated character
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being checked
         *    ruleParams      - an array holding the required rule parameters.
         *
         *                          caseSensitive (boolean) [0]:  The first parameter must contain a
         *                              Boolean value that indicates whether or not the value check
         *                              should be case-sensitive (e.g., "Aa" is not considered to be
         *                              "repeating"). If no value is provided, the value is assumed
         *                              to be FALSE.
         *
         *                          alphaOnly (boolean) [1]:  The second parameter must contain a
         *                              Boolean value that indicates whether or not the check should
         *                              ignore non-alphanumeric characters (e.g., "(111) 111-1111"
         *                              would be checked as "1111111111"). If no value is provided,
         *                              the value is assumed to be FALSE.
         *
         * Returns:
         *    false - if the input field is made up of only a single, repeating character
         *    true  - if the input field contains two or more different character
         */
        isNotSingleRepeatingChar($element, ruleParams) {
          let options = {
            caseSensitive: false,
            alphaOnly: false
          };
          let sElementVal = $element.val();
          let isValidInput = true;
          options = validator.fn.helper.getDefinedParams(ruleParams, options);

          /* if the first element in "aFieldRuleValue" is true, make the regular expression case
            insensitive */
          const regexConstructorFlag = options.caseSensitive ? '' : 'i';

          /* if the second element in "aFieldRuleValue" is true, remove non-alphanumeric
            characters from the value before validating */
          if (options.alphaOnly) {
            sElementVal = sElementVal.replace(/[^a-z\d]/ig, '');
          }
          const regexSingleRepeatedCharPattern = new RegExp('^(.)\\1+$', regexConstructorFlag);

          /* fail the validation if the value passes the "single repeating character" regex
            check */
          if (validator.fn.helper.matchesPattern(sElementVal, regexSingleRepeatedCharPattern)) {
            const elementValue = $element.val();
            isValidInput = {
              ruleName: 'isNotSingleRepeatingChar',
              ruleParams: {
                elementValue
              }
            };
          }
          return isValidInput;
        },
        /* "validate.sameAsField" method
         * "sameAsField" validates that the value of an input matches the value of another input
         * field (passed in as a parameter).
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding the required rule parameters.
         *
         *                      compareToField (string) [0]: Must contain a string representation of
         *                          the ID attribute of another input whose value must match the
         *                          value of $element.
         *
         *                      caseSensitive (boolean) [1]: The second item in the array is a
         *                          boolean representing whether or not the value comparison should
         *                          be case sensitive.
         *
         * Returns:
         *    true  - if the values to the two fields are the same
         *    false - if the values to the two fields are not the same
         */
        sameAsField($element, ruleParams) {
          let options = {
            $compareToField: null,
            caseSensitive: true
          };
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          options.$compareToField = typeof options.$compareToField !== 'undefined' ? $(`#${options.$compareToField}`) : options.$compareToField;
          if (!options.caseSensitive) {
            return $element.val().toLowerCase() === options.$compareToField.val().toLowerCase();
          }
          return $element.val() === options.$compareToField.val();
        },
        /* "validate.notSameAsField" method
         * "notSameAsField" validates that the value of an input does NOT match the value of
         * another input field (passed in as a parameter).
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding the required rule parameters.
         *
         *                      compareToField (string) [0]: Must contain a string representation of
         *                          the ID attribute of another input whose value must NOT match the
         *                          value of $element.
         *
         *                      caseSensitive (boolean) [1]: The second item in the array is a
         *                          boolean representing whether or not the value comparison should
         *                          be case sensitive.
         *
         * Returns:
         *    true  - if the values to the two fields are not the same
         *    false - if the values to the two fields are the same
         */
        notSameAsField($element, ruleParams) {
          let options = {
            $compareToField: null,
            caseSensitive: true
          };
          options = validator.fn.helper.getDefinedParams(ruleParams, options);
          options.$compareToField = typeof options.$compareToField !== 'undefined' ? $(`#${options.$compareToField}`) : options.$compareToField;
          if (!options.caseSensitive) {
            return $element.val().toLowerCase() !== options.$compareToField.val().toLowerCase();
          }
          return $element.val() !== options.$compareToField.val();
        },
        /* "validate.isNotValue" method
         * "isNotValue" validates that the value of an input does NOT match a value contained in a
         * specified array of values (passed in as a parameter).
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding the required rule parameters.
         *
         *                      invalidValues (Array): Must contain one or more string values
         *                          representing properly formatted, but disallowed values.
         *
         * Returns:
         *    true  - if the value does not match one of the invalid values in the array
         *    false - if the value matches one of the invalid values in the array
         */
        isNotValue($element, ruleParams) {
          let options = {
            invalidValues: null
          };
          if (ruleParams && ruleParams instanceof Array) {
            options.invalidValues = ruleParams;
          } else {
            options.invalidValues = typeof ruleParams.invalidValues !== 'undefined' ? ruleParams.invalidValues : options.invalidValues;
          }
          return $.inArray($element.val(), options.invalidValues) === -1;
        },
        /* "validate.customPattern" method
         * "customPattern" validates that the value of an input  matches character pattern that
         * is unique to the field
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters.
         *
         *                      regexPattern (RegExp/string) [0]: The first parameter must contain a
         *                          JavaScript Regex object or string value to be used as the first
         *                          parameter of the RegExp constructor.
         *
         *                      regexFlags (string) [1]: The second parameter is optional and may
         *                          contain regex flags for use as the second parameter of the
         *                          RegExp constructor.  This value will be ignored if the first
         *                          parameter is a RegExp object.
         *
         *                      The parameters must result in a RegExp object that enforces the
         *                      pattern that all values for the field must match in order to be
         *                      valid.
         *
         * Returns:
         *    true  - if the value of the input value matches the custom pattern
         *    false - if the value of the input value does not match the custom pattern
         */
        customPattern($element, ruleParams) {
          const options = {
            ruleParamsLength: null,
            regexCustomFlags: '',
            regexCustomPattern: null
          };
          if (ruleParams && ruleParams instanceof Array) {
            options.ruleParamsLength = ruleParams.length;
            /* if the array has a second value, use it as the flags for the RegExp constructor */
            options.regexCustomFlags = options.ruleParamsLength >= 2 && typeof ruleParams[1] === 'string' ? ruleParams[1] : options.regexCustomFlags;
            options.regexCustomPattern = typeof ruleParams[0] !== 'undefined' ? ruleParams[0] : options.regexCustomPattern;
          } else if (ruleParams && ruleParams instanceof Object) {
            options.ruleParamsLength = Object.keys(ruleParams).length;
            /* if the object has a second value, use it as the flags for the RegExp constructor */
            options.regexCustomFlags = options.ruleParamsLength >= 2 && typeof ruleParams.regexFlags === 'string' ? ruleParams.regexFlags : options.regexCustomFlags;
            options.regexCustomPattern = typeof ruleParams.regexPattern !== 'undefined' ? ruleParams.regexPattern : options.regexCustomPattern;
          }

          /* if the array has at least one value, check the type and create or retrieve the RegExp
            object */
          if (options.ruleParamsLength > 0) {
            /* if the first element in "ruleParams" is a RegExp object, copy the values into
              "regexCustomPattern" */
            if (typeof options.regexCustomPattern === 'string') {
              /* if the first element in "ruleParams" is a string, retrieve the string pattern
                and RegExp flags (if present), create a new RegExp object, and assign it to
                "regexCustomPattern" */
              options.regexCustomPattern = new RegExp(options.regexCustomPattern, options.regexCustomFlags);
            } else if (!(options.regexCustomPattern instanceof RegExp)) {
              /* if the first element in "ruleParams" isn't in a valid format, fail the
                validation */
              return false;
            }
          } else {
            /* if the array is empty, fail the validation */
            return false;
          }
          return validator.fn.helper.matchesPattern($element, options.regexCustomPattern);
        },
        /* "validate.customFunction" method
         * "customFunction" validates that the value of an input matches validation rules that are
         * unique to the field
         *
         * Parameters:
         *    $element        - a jQuery reference to the input element whose value is being
         *                      checked
         *    ruleParams      - an array/object holding any rule parameters.
         *
         *                      customFunction (Function) [0]: This parameter contains a reference
         *                          to the function to be called by this validation.  The function
         *                          must return a boolean value or an object with the following
         *                          example format:
         *
         *                              {
         *                                ruleName: 'myCustomFunction',
         *                                ruleParams: ['parameter1', 'parameter2', false],
         *                                errorMessage: 'My Custom Error Message'
         *                              }
         *
         *                          NOTE: It is possible to return an existing ruleName to display
         *                              the default error message for that rule, unless you provide
         *                              a custom error message within the object or if the JCR
         *                              contains a custom error message.  If the error message
         *                              uses variables the values provided within ruleParams will be
         *                              used.
         *
         *                      functionParams (Array/Object) [1]: Depending on whether or not you
         *                          provide an Array or an Object, your function must be designed
         *                          to accept the appropriate type.
         *
         *                      includeElement (boolean) [2]: A boolean value that determines
         *                          wether or not to include the $element value as an argument to
         *                          the custom function. [Default: false]
         *
         * Returns:
         *    true  - if the value of the custom function returns true
         *    false - if the value of the custom function returns false
         */
        customFunction($element, ruleParams) {
          let options = {
            customFunction: null,
            functionParams: [],
            includeElement: false
          };
          let functionResult = false;
          if (ruleParams && ruleParams instanceof Array) {
            options.customFunction = typeof ruleParams[0] !== 'undefined' ? ruleParams[0] : options.customFunction;
            options.functionParams = options.functionParams.concat(ruleParams.slice(1, -1));
          } else if (ruleParams && ruleParams instanceof Object) {
            options.customFunction = typeof ruleParams.customFunction !== 'undefined' ? ruleParams.customFunction : options.customFunction;
            options.includeElement = typeof ruleParams.includeElement !== 'undefined' ? ruleParams.includeElement : options.includeElement;
            if (ruleParams.functionParams && ruleParams.functionParams instanceof Array) {
              options.functionParams = options.functionParams.concat(ruleParams.functionParams);
            } else if (ruleParams.functionParams instanceof Object) {
              Object.keys(ruleParams.functionParams).forEach(parameter => {
                options.functionParams.push(ruleParams.functionParams[parameter]);
              });
            }
          }
          if (options.includeElement) {
            options.functionParams.unshift($element);
          }
          if (options.customFunction && typeof options.customFunction === 'function') {
            if (options.functionParams.length > 0) {
              functionResult = options.customFunction.apply(this, options.functionParams);
            } else {
              functionResult = options.customFunction($element);
            }
          }
          return functionResult;
        }
      },
      /* "fn.core" methods - the core functions in the library that set up the validation rules
        fields and execute the validations, when triggered */
      core: {
        /* "core.bindValidationRules" method
         * "bindValidationRules" retrieves all of the validation rules and optional custom
         * messages for an input field or collection of inputs and registers them for validation
         * on the page.
         *
         * Parameters:
         *    oValidationRuleset - an object that contains the ID values of any inputs that
         *                         require validation, the events that should trigger the
         *                         validations, and the validation rules (including parameters
         *                         and optional custom error messages).  The object should be in
         *                         the following format:
         *
         *                         {
         *                           "input_ID": {
         *                             "event_name": [{
         *                               "validation_rule_name": {
         *                                 "parameters": {
         *                                   "foo": "bar",
         *                                   "bar": true, ...
         *                                 },
         *                                 "errorMessage": "Custom error message (optional)"
         *                               }
         *                             }]
         *                           }
         *                         }
         *
         *    bAddToBeginning    - a Boolean value indicating whether or not the validation rules
         *                         being added should be checked before any existing rules within
         *                         the same field and event.  If no value is provided, the value
         *                         is assumed to be FALSE
         *
         * Returns:       - none -
         */
        bindValidationRules(oValidationRuleset, bAddToBeginning) {
          /* Process the rules for each input field */
          Object.keys(oValidationRuleset).forEach(sCurrentInput => {
            if (Object.prototype.hasOwnProperty.call(oValidationRuleset, sCurrentInput)) {
              /* If the SpartaValidator.data.oValidationRules does not already contain any rules
                for this input, create an entry for the input */
              if (!(validator.data.oValidationRules[sCurrentInput] instanceof Object)) {
                validator.data.oValidationRules[sCurrentInput] = {};
              }

              /* Process the rules for each event on the input */
              const oCurrentInput = oValidationRuleset[sCurrentInput];
              Object.keys(oCurrentInput).forEach(sCurrentEvent => {
                if (Object.prototype.hasOwnProperty.call(oCurrentInput, sCurrentEvent)) {
                  /* If the input already has an entry for this event in
                    SpartaValidator.data.oValidationRules, add the new validations to the existing
                    rules */
                  if (validator.data.oValidationRules[sCurrentInput][sCurrentEvent] instanceof Object) {
                    /* If the new validations need to be checked before the existing rules,
                      prepend them */
                    if (bAddToBeginning) {
                      validator.data.oValidationRules[sCurrentInput][sCurrentEvent] = $.extend(true, {}, validator.data.oValidationRules[sCurrentInput][sCurrentEvent], oCurrentInput[sCurrentEvent]);
                    } else {
                      /* Append the new validations to the existing rules */
                      validator.data.oValidationRules[sCurrentInput][sCurrentEvent] = $.extend(true, {}, oCurrentInput[sCurrentEvent], validator.data.oValidationRules[sCurrentInput][sCurrentEvent]);
                    }
                    /* If the input does not have an entry for this event in
                      SpartaValidator.data.oValidationRules, create a new entry for the event, add
                      the validation rules, and bind the validation check to the input. */
                  } else if (oCurrentInput[sCurrentEvent] instanceof Array) {
                    /* Loop through the Array of Rules and create a new entry in
                      SpartaValidator.data.oValidationRules for the event and add the validation
                      rules */
                    oCurrentInput[sCurrentEvent].forEach(rule => {
                      /* If the new validations need to be checked before the existing rules,
                        prepend them */
                      if (bAddToBeginning) {
                        validator.data.oValidationRules[sCurrentInput][sCurrentEvent] = $.extend(true, {}, validator.data.oValidationRules[sCurrentInput][sCurrentEvent], rule);
                      } else {
                        /* Append the new validations to the existing rules */
                        validator.data.oValidationRules[sCurrentInput][sCurrentEvent] = $.extend(true, {}, rule, validator.data.oValidationRules[sCurrentInput][sCurrentEvent]);
                      }
                    });
                  } else {
                    /* Create new entry in SpartaValidator.data.oValidationRules for the event and
                      add the validation rules */
                    validator.data.oValidationRules[sCurrentInput][sCurrentEvent] = oCurrentInput[sCurrentEvent];
                  }
                }

                /* Add rule to the pending RULES array for current input */
                Object.keys(validator.data.oValidationRules[sCurrentInput][sCurrentEvent]).forEach(oValidationRule => {
                  if (validationRulesImporter.RULES[oValidationRule] && !validationRulesImporter.RULES.pending.includes(oValidationRule)) {
                    validationRulesImporter.RULES.pending.push(oValidationRule);
                  }
                });

                /* Bind the "validateFieldOnEvent" method to the input, to fire on the specified
                  event */
                const $currentInput = $(`#${sCurrentInput}`);
                const sNamespacedEvent = `${sCurrentEvent}.${validator.data.globals.VALIDATOR_NAMESPACE}`;

                /* To cover cases where the event is already bound to the input, unbind so that
                  two listeners are not created */
                $currentInput.off(sNamespacedEvent);

                /* Bind a new "validateFieldOnEvent" method to the current event */
                $currentInput.on(sNamespacedEvent, function (e) {
                  validator.fn.core.validateFieldOnEvent($(this), e);
                });
              });
            }
          });

          /* Get validation RULES lists for all rules if a list exists for the rule */
          validationRulesImporter.getAllPendingRules();
        },
        /* "core.updateValidationRules" method
         * "updateValidationRules" updates existing validations rules and optional custom
         * messages for an input field or collection of inputs.  If an event or a rule does not
         * exist for an input, the function will append them to the existing input or event
         * (respectively), if the "bAddIfMissing" parameter is set to "true".  If the input does
         * not currently have a ruleset, the input will be added using the standard
         * "bindValidationRules" method, if the "bAddIfMissing" parameter is set to "true".
         *
         * Parameters:
         *    oValidationRuleset - an object that contains the ID values of any inputs that
         *                         require validation, the events that should trigger the
         *                         validations, and the validation rules (including parameters
         *                         and optional custom error messages).  The object should be in
         *                         the following format:
         *
         *                         {
         *                           "input_ID": {
         *                             "event_name": {
         *                               "validation_rule_name": {
         *                                 "parameters": ["foo", "bar", ...],
         *                                 "errorMessage": "Custom error message (optional)"
         *                               }
         *                             }
         *                           }
         *                         }
         *
         *    bAddIfMissing    - a Boolean value indicating whether or not updated values should
         *                       be added, if they do not already exist in the validation rules.
         *                       If no value is provided, the value is assumed to be FALSE
         *
         * Returns:       - none -
         */
        updateValidationRules(oValidationRuleset, bAddIfMissing) {
          /* Process the rules for each input field */
          Object.keys(oValidationRuleset).forEach(sCurrentInput => {
            if (Object.prototype.hasOwnProperty.call(oValidationRuleset, sCurrentInput)) {
              /* If the SpartaValidator.data.oValidationRules does not already contain any rules
                for this input AND "bAddIfMissing" is "true", call "bindValidationRules" and pass
                it the rules object for the input */
              if (!(validator.data.oValidationRules[sCurrentInput] instanceof Object)) {
                if (bAddIfMissing) {
                  const oNewInputRuleset = {};
                  oNewInputRuleset[sCurrentInput] = oValidationRuleset[sCurrentInput];
                  validator.fn.core.bindValidationRules(oNewInputRuleset, false);
                }
              } else {
                /* Process the rules for each event on the input */
                const oCurrentInput = oValidationRuleset[sCurrentInput];
                Object.keys(oCurrentInput).forEach(sCurrentEvent => {
                  if (Object.prototype.hasOwnProperty.call(oCurrentInput, sCurrentEvent)) {
                    /* If the input does not already have an entry for this event in
                      SpartaValidator.data.oValidationRules AND "bAddIfMissing" is "true", add the
                      new event and its rules to the existing input */
                    if (!(validator.data.oValidationRules[sCurrentInput][sCurrentEvent] instanceof Object)) {
                      if (bAddIfMissing) {
                        const oNewEventRuleset = {};
                        oNewEventRuleset[sCurrentInput] = {};
                        oNewEventRuleset[sCurrentInput][sCurrentEvent] = oCurrentInput[sCurrentEvent];
                        validator.fn.core.bindValidationRules(oNewEventRuleset, false);
                      }
                    } else {
                      /* Process the rules for each event on the input */
                      const oCurrentEvent = oCurrentInput[sCurrentEvent];
                      Object.keys(oCurrentEvent).forEach(sCurrentRule => {
                        if (Object.prototype.hasOwnProperty.call(oCurrentEvent, sCurrentRule)) {
                          /* If the event already has an entry for this rule in
                            SpartaValidator.data.oValidationRules OR it does not  have an entry,
                            but "bAddIfMissing" is "true", add the new rule configuration to the
                            existing event */
                          if (validator.data.oValidationRules[sCurrentInput][sCurrentEvent][sCurrentRule] instanceof Object || bAddIfMissing) {
                            validator.data.oValidationRules[sCurrentInput][sCurrentEvent][sCurrentRule] = oCurrentEvent[sCurrentRule];
                          }
                        }
                      });
                    }
                  }
                });
              }
            }
          });
        },
        /* "core.removeValidationsByInput" method
         * "removeValidationsByInput" removes all of the validation rules for a group of
         * specified input elements.
         *
         * Parameters:
         *    $inputGroup - a jQuery reference to a group of HTML input fields whose validation
         *                  rules should be removed
         *
         * Returns:       - none -
         */
        removeValidationsByInput($inputGroup) {
          /* Delete all of the validations for the specified inputs */
          $inputGroup.each(function () {
            const $currentInput = $(this);
            const sFieldId = $currentInput.attr('id');

            /* If there is an entry in SpartaValidator.data.oValidationRules for the current
              input, delete all of the validations in it, for the all of the events */
            if (Object.prototype.hasOwnProperty.call(validator.data.oValidationRules, sFieldId)) {
              /* Process the rules for each event on the current input */
              const oCurrentInput = validator.data.oValidationRules[sFieldId];
              Object.keys(oCurrentInput).forEach(sCurrentEvent => {
                if (Object.prototype.hasOwnProperty.call(oCurrentInput, sCurrentEvent)) {
                  /* Remove the current event binding from the current input */
                  $currentInput.off(`${sCurrentEvent}.${validator.data.globals.VALIDATOR_NAMESPACE}`);
                }
              });

              /* Remove the entire section of validator.data.oValidationRules for the current
                input */
              delete validator.data.oValidationRules[sFieldId];
            }
          });
        },
        /* "core.removeValidationsByEvent" method
         * "removeValidationsByEvent" removes all of the validation rules for the specified
         * events for an input element.
         *
         * Parameters:
         *    $inputField - a jQuery reference to the HTML input field whose validation rules
         *                  should be removed
         *    aEventNames - an array of strings that represent the event names whose validation
         *                  rules should be removed
         *
         * Returns:       - none -
         */
        removeValidationsByEvent($inputField, aEventNames) {
          const sFieldId = $inputField.attr('id');

          /* If there is an entry in SpartaValidator.data.oValidationRules for the specified
            input, delete all of the validations in it, for the specified events */
          if (Object.prototype.hasOwnProperty.call(validator.data.oValidationRules, sFieldId)) {
            const oInputField = validator.data.oValidationRules[sFieldId];

            /* Delete all of the validations for the the specified events, in the specified
              input */
            for (let iEventIndex = 0; iEventIndex < aEventNames.length; iEventIndex += 1) {
              /* Remove the current event binding from the current input */
              $inputField.off(`${aEventNames[iEventIndex]}.validator.data.globals.VALIDATOR_NAMESPACE`);

              /* Remove the entire section of SpartaValidator.data.oValidationRules for the
                current event */
              delete oInputField[aEventNames[iEventIndex]];
            }
          }
        },
        /* "core.removeValidationsByRule" method
         * "removeValidationsByRule" removes all of the specified validation rules for an event
         * on an input element.
         *
         * Parameters:
         *    $inputField - a jQuery reference to the HTML input field whose validation rules
         *                  should be removed
         *    sEventName  - a strings that represents the event name whose validation rules
         *                  should be removed
         *    aRuleNames  - an array of strings that represent the validation rule names that
         *                  should be removed
         *
         * Returns:       - none -
         */
        removeValidationsByRule($inputField, sEventName, aRuleNames) {
          const sFieldId = $inputField.attr('id');

          /* If there is an entry in SpartaValidator.data.oValidationRules for the specified
            input and event, delete all of the specified validations rules in it */
          if (Object.prototype.hasOwnProperty.call(validator.data.oValidationRules, sFieldId) && Object.prototype.hasOwnProperty.call(validator.data.oValidationRules[sFieldId], sEventName)) {
            const oCurrentEvent = validator.data.oValidationRules[sFieldId][sEventName];

            /* Remove each rule for the specified input and event */
            for (let iRuleIndex = 0; iRuleIndex < aRuleNames.length; iRuleIndex += 1) {
              delete oCurrentEvent[aRuleNames[iRuleIndex]];
            }
          }
        },
        /* "core.validateFieldOnEvent" method
         * "validateFieldOnEvent" retrieves all of the validation rules that are bound to an
         * input field for a specific event, and triggers each one, to see if the current value
         * of the input passes them all.
         *
         * Parameters:
         *    $inputField - a jQuery reference to HTML input field that needs to be validated
         *    event -       a jQuery event that has been set up to trigger validation rules on
         *                  an input
         *
         * Returns:       - none -
         */
        validateFieldOnEvent($inputField, event) {
          const sFieldId = $inputField.attr('id');
          const sTriggeringEvent = event.type;
          if (Object.prototype.hasOwnProperty.call(validator.data.oValidationRules, sFieldId) && Object.prototype.hasOwnProperty.call(validator.data.oValidationRules[sFieldId], sTriggeringEvent)) {
            /* Process each validation rule for the field, one at a time */
            const oCurrentRuleset = validator.data.oValidationRules[sFieldId][sTriggeringEvent];
            validator.fn.core.validateRules($inputField, oCurrentRuleset);
          }
        },
        /* "core.validateAllFieldRules" method
         * "validateAllFieldRules" retrieves all of the validation rules, for all events that
         * are bound to an input field, and checks to see if the current value of the input
         * passes them all.  If the validation for an event fails, the validation checks for any
         * additional events are skipped.
         *
         * Parameters:
         *    $inputField - a jQuery reference to HTML input field that needs to be validated
         *
         * Returns:       - none -
         */
        validateAllFieldRules($inputField) {
          let bIsValidField = true;
          const sFieldId = $inputField.attr('id');

          /* Process each validation rule for the field, one at a time */
          const oCurrentInput = validator.data.oValidationRules[sFieldId];
          if (oCurrentInput) {
            Object.keys(oCurrentInput).forEach(sCurrentEvent => {
              if (bIsValidField && Object.prototype.hasOwnProperty.call(oCurrentInput, sCurrentEvent)) {
                /* Process each validation rule for the field, one at a time */
                const oCurrentRuleset = validator.data.oValidationRules[sFieldId][sCurrentEvent];
                bIsValidField = validator.fn.core.validateRules($inputField, oCurrentRuleset);
              }
            });
          }
        },
        /* "core.validateRules" method
         * "validateRules" checks to see if the current value of a specified input passes each
         * validation in a specified group of rules.  If the input fails a validation rule, the
         * processing stops, the field is flagged as having an error, and an error message
         * associated with the validation rule is displayed.
         *
         * Parameters:
         *    $inputField     - a jQuery reference to HTML input field that needs to be validated
         *    oCurrentRuleset - an object that contains the current set of rules (from
         *                      SpartaValidator.data.oValidationRules) that the value in the
         *                      input is being tested against
         *
         * Returns:       - none -
         */
        validateRules($inputField, oCurrentRuleset) {
          let bIsValidValue = true;

          /* Process the each rule in the ruleset */
          Object.keys(oCurrentRuleset).every(sCurrentRule => {
            if (Object.prototype.hasOwnProperty.call(oCurrentRuleset, sCurrentRule)) {
              const oCurrentRule = oCurrentRuleset[sCurrentRule];

              /* Only check the validation if input field has a value */
              if (!validator.fn.helper.isEmptyField($inputField)) {
                /* If the current validation rule contains a namespaced validation rule */
                if (sCurrentRule.indexOf('.') !== -1) {
                  const nsCurrentNameSpace = sCurrentRule.match(/^([^.]*)/)[0];
                  const nsCurrentRule = sCurrentRule.match(/([^.]*$)/)[0];

                  /* If the current namespaced validation rule has a method defined in the
                    referenced namespace for it, process the validation */
                  if (nsCurrentNameSpace in validator.fn.validate && nsCurrentRule in validator.fn.validate[nsCurrentNameSpace]) {
                    bIsValidValue = validator.fn.validate[nsCurrentNameSpace][nsCurrentRule]($inputField, oCurrentRule.parameters);
                  }
                } else if (sCurrentRule in validator.fn.validate) {
                  /* If the current validation rule has a method defined for it, process the
                    validation */
                  bIsValidValue = validator.fn.validate[sCurrentRule]($inputField, oCurrentRule.parameters);
                }
                /* If the value failed to pass the current validation rule, process the error */
                if (!bIsValidValue || bIsValidValue instanceof Object) {
                  let sErrorMessage = '';
                  let sCurrentRuleName = sCurrentRule;
                  let aCurrentParams = oCurrentRule.parameters;
                  if (bIsValidValue instanceof Object) {
                    if (bIsValidValue.ruleName) {
                      sCurrentRuleName = bIsValidValue.ruleName;
                    }
                    if (bIsValidValue.ruleParams) {
                      aCurrentParams = bIsValidValue.ruleParams;
                    }
                    if (bIsValidValue.errorMessage) {
                      sErrorMessage = validator.fn.helper.isValidStringValue(oCurrentRule.errorMessage) ? oCurrentRule.errorMessage : bIsValidValue.errorMessage;
                    } else {
                      sErrorMessage = validator.fn.helper.isValidStringValue(oCurrentRule.errorMessage) ? oCurrentRule.errorMessage : validator.messages[bIsValidValue.ruleName];
                    }
                    bIsValidValue = false;
                  } else {
                    /* Get the language-specific error message for the current validation rule */
                    sErrorMessage = validator.fn.helper.isValidStringValue(oCurrentRule.errorMessage) ? oCurrentRule.errorMessage : validator.messages[sCurrentRule];
                  }

                  /* If the it is not a custom message and the default message contains multiple
                    options select the correct option based on the parameters */
                  if (sErrorMessage instanceof Object) {
                    if (sCurrentRuleName === 'nameCharacters') {
                      if (aCurrentParams && aCurrentParams[0] === false) {
                        sErrorMessage = sErrorMessage.noSpecialChars;
                      } else {
                        sErrorMessage = sErrorMessage.allowSpecialChars;
                      }
                    }
                    if (sCurrentRuleName === 'dateFormat') {
                      sErrorMessage = sErrorMessage[oCurrentRule.parameters[0]];
                    }
                  }

                  /* Get the error message has placeholders, replace them with values from the
                    parameters for the rule */
                  if (/\$/g.test(sErrorMessage)) {
                    /* Split multiple values in a parameter into an array */
                    const aDynamicVals = aCurrentParams;
                    let iDynamicValLength = aDynamicVals.length;
                    if (aDynamicVals instanceof Object) {
                      const oDynamicKeys = Object.keys(aDynamicVals);
                      iDynamicValLength = oDynamicKeys.length;
                      for (let curVal = 0; curVal < iDynamicValLength; curVal += 1) {
                        let sCurVal = aDynamicVals[oDynamicKeys[curVal]];
                        const sCurValKey = oDynamicKeys[curVal];

                        /* If the current validation rule is date related, convert the offset
                          value in the parameter to an actual date, based on the current date */
                        if (/date/i.test(sCurrentRule)) {
                          const sDateType = aDynamicVals.dateType;
                          sCurVal = validator.fn.helper.formatDate(validator.fn.helper.getOffsetDate(sCurVal, sDateType), sDateType);
                        } else if (/sameasfield/i.test(sCurrentRule)) {
                          sCurVal = $(`label[for="${aDynamicVals[0]}"]`).clone().children().remove().end().text();
                        } else if (sCurrentRule === 'isNotValue') {
                          sCurVal = $(`label[for="${$inputField.attr('id')}"]`).clone().children().remove().end().text();
                        }

                        /* For each parameter value, replace the associated placeholder in the error
                          message, added support for Object keys as variables within the message */
                        sErrorMessage = sErrorMessage.replace(`$${curVal}`, sCurVal).replace(`$${sCurValKey}`, sCurVal);
                      }
                    } else {
                      /* For each parameter value, replace the associated placeholder in the error
                        message */
                      for (let curVal = 0; curVal < iDynamicValLength; curVal += 1) {
                        let sCurVal = aDynamicVals[curVal];

                        /* If the current validation rule is date related, convert the offset
                          value in the parameter to an actual date, based on the current date */
                        if (/date/i.test(sCurrentRule)) {
                          const sDateType = aDynamicVals[iDynamicValLength - 1];
                          sCurVal = validator.fn.helper.formatDate(validator.fn.helper.getOffsetDate(sCurVal, sDateType), sDateType);
                        } else if (/sameasfield/i.test(sCurrentRule)) {
                          sCurVal = $(`label[for="${aDynamicVals[0]}"]`).clone().children().remove().end().text();
                        } else if (sCurrentRule === 'isNotValue') {
                          sCurVal = $(`label[for="${$inputField.attr('id')}"]`).clone().children().remove().end().text();
                        }
                        sErrorMessage = sErrorMessage.replace(`$${curVal}`, sCurVal);
                      }
                    }
                  }

                  /* Flagged input field as having an error and display the error message  */
                  validator.fn.core.handleFieldError($inputField, sCurrentRule, sErrorMessage);
                } else {
                  validator.fn.core.clearFieldError($inputField, sCurrentRule);
                }
              } else {
                validator.fn.core.clearFieldError($inputField, sCurrentRule);
              }
            }

            /* Break out of the loop when it fails a validation */
            return bIsValidValue;
          });

          /* Return whether or not the field value passed all of the validations */
          return bIsValidValue;
        },
        /* "core.validateRequiredFields" method
         * "validateRequiredFields" checks all of the input fields that are passed in to see if a
         * value has been provided.
         *
         * Parameters:   $inputGroup - a jQuery selection containing all of the elements that
         *                             should be included in the required field check
         *
         * Returns:
         *    true  - if all required fields have a value
         *    false - if at least one of the required fields does not contain a value
         */
        validateRequiredFields(inputGroup) {
          let bAllRequiredFieldsFull = true;
          // Check if we've been given a vanilla JavaScript array or jQuery array.
          const $validationFields = !(inputGroup instanceof $) ? $(inputGroup) : inputGroup;

          // Check each of the fields in the set to see if it has a value.
          $validationFields.each(function (item) {
            // check if items within fieldset are required
            const sNodeType = $(this).closest('.spa-fieldset').length > 0 && ($(this)[0].type === 'checkbox' || $(this)[0].type === 'radio') ? $(this).closest('.spa-fieldset')[0].nodeName.toLowerCase() : $(this)[0].nodeName.toLowerCase();
            const element = sNodeType === 'fieldset' ? $(this).closest('.spa-fieldset') : this;

            /* If the current required element is a fieldset, check to see if there is a checked
              radio button or checkbox inside it */
            if (sNodeType === 'fieldset' && $(element).find('input').filter(':checked').length < 1 || (/* If it is just and input, check to see if it has a value */
            sNodeType !== 'fieldset' && (/* If the current required element is a checkbox element, see if it is checked */
            $(this).attr('type') === 'checkbox' && !$(this).prop('checked') || (/* For all other inputs, check to see if the they contain a value */
            validator.fn.helper.isEmptyField($(this)))))) {
              validator.fn.core.handleRequiredError($(element), $(element).data('required-error-message') || validator.messages.required);
              bAllRequiredFieldsFull = false;
            } else {
              validator.fn.core.clearRequiredError($(element));
            }
          });
          return bAllRequiredFieldsFull;
        },
        /* "core.collectErrorData" method
         * "collectErrorData" combines together a standard group of information about a failed
         * validation.
         *
         * Parameters:
         *    $inputField      - a jQuery reference to HTML input field that failed validation
         *    sFailedRule      - a string that contains the framework validation rule that failed
         *    sErrorMessage    - a string that contains the error message for the validation
         *                       that failed
         *
         * Returns: oErrorData - an object containing core information about an input validation
         *                       failure
         */
        collectErrorData($inputField, sFailedRule, sErrorMessage) {
          const isFieldSet = $inputField[0].nodeName.toLowerCase() === 'fieldset';
          const $errorLegend = $(`legend[for='${$inputField.attr('id')}']`);
          const $errorLabel = $(`label[for='${$inputField.attr('id')}']`);
          const $label = isFieldSet ? $errorLegend : $errorLabel;
          return {
            field: $inputField,
            rule: sFailedRule,
            message: sErrorMessage || '',
            label: $label
          };
        },
        /* "core.validateAllFields" method
         * "validateAllFields" validates all fields with rules in
         * SpartaValidator.data.oValidationRules.
         *
         * Parameters:  - none -
         *
         * Returns: - none -
         */
        validateAllFields() {
          $.each(validator.data.oValidationRules, function (key, value) {
            validator.fn.core.validateAllFieldRules($(`#${key}`));
          });
        },
        /* "core.handleFieldError" method
         * "handleFieldError" displays the treatment for input validation failures and triggers a
         * Sparta event to broadcast that a specific validation rule failed for the field.
         *
         * Parameters:   $inputField       - a jQuery reference to the input element that failed
         *                                   validation
         *               sValidationRule   - a string that contains the framework validation rule
         *                                   that failed
         *               sErrorMessage     - an error message containing details on the
         *                                   validation that failed
         *
         * Returns: - none -
         */

        handleFieldError($inputField, sValidationRule, sErrorMessage) {
          /* clear error for ADA fix */
          document.getElementById(`${$inputField[0].id}${'_errorMessage'}`).innerHTML = '';
          /* collect data about the validation to pass to the failure event */
          const oErrorData = validator.fn.core.collectErrorData($inputField, sValidationRule, sErrorMessage);

          /* add standard classes for invalid field values to field and field label */
          $inputField.addClass(validator.data.globals.INPUT_ERROR_BASE_CLASS + validator.data.globals.VALIDATION_ERROR_SUFFIX);
          oErrorData.label.addClass(validator.data.globals.LABEL_ERROR_BASE_CLASS + validator.data.globals.VALIDATION_ERROR_SUFFIX);

          /* should be configurable to allow the calling a custom function within the app */
          spartaEventsUtility.pub(validator.EVENTS.VALIDATION_FAILURE_EVENT, oErrorData);
        },
        /* "core.clearFieldError" method
         * "clearFieldError" removes the treatment for input validation failures and triggers a
         * Sparta event to broadcast that a specific validation rule was successful for the
         * field.
         *
         * Parameters:   $inputField       - a jQuery reference to the input element that failed
         *                                   validation
         *               sValidationRule   - a string that contains the framework validation rule
         *                                   that failed
         *
         * Returns: - none -
         */
        clearFieldError($inputField, sValidationRule) {
          /* collect data about the validation to pass to the success event */
          const oErrorData = validator.fn.core.collectErrorData($inputField, sValidationRule);

          /* remove standard classes for invalid field values to field and field label */
          $inputField.removeClass(validator.data.globals.INPUT_ERROR_BASE_CLASS + validator.data.globals.VALIDATION_ERROR_SUFFIX);
          oErrorData.label.removeClass(validator.data.globals.LABEL_ERROR_BASE_CLASS + validator.data.globals.VALIDATION_ERROR_SUFFIX);

          /* should be configurable to allow the calling a custom function within the app */
          spartaEventsUtility.pub(validator.EVENTS.VALIDATION_SUCCESS_EVENT, oErrorData);
        },
        /* "core.handleRequiredError" method
         * "handleRequiredError" displays the treatment for required field failures and triggers
         * a Sparta event to broadcast that the required field check failed for the field.
         *
         * Parameters:   $inputField       - a jQuery reference to the input element that failed
         *                                   validation
         *               sErrorMessage     - an error message containing details on the
         *                                   validation that failed
         *
         * Returns: - none -
         */
        handleRequiredError($inputField, sErrorMessage) {
          /* collect data about the validation to pass to the failure event */
          const oErrorData = validator.fn.core.collectErrorData($inputField, validator.data.globals.REQUIRED_FIELD_RULE_NAME, sErrorMessage);

          /* add standard classes for missing required field values to field and field label */
          $inputField.addClass(validator.data.globals.INPUT_ERROR_BASE_CLASS + validator.data.globals.REQUIRED_ERROR_SUFFIX);
          oErrorData.label.addClass(validator.data.globals.LABEL_ERROR_BASE_CLASS + validator.data.globals.REQUIRED_ERROR_SUFFIX);

          /* should be configurable to allow the calling a custom function within the app */
          spartaEventsUtility.pub(validator.EVENTS.REQUIRED_FIELD_FAILURE_EVENT, oErrorData);
        },
        /* "core.clearRequiredError" method
         * "clearRequiredError" removes the treatment for required field failures and triggers a
         * Sparta event to broadcast that the required field check was successful for the field.
         *
         * Parameters:   $inputField   - a jQuery reference to the input element that failed
         *                               validation
         *
         * Returns: - none -
         */
        clearRequiredError($inputField) {
          /* collect data about the validation to pass to the failure event */
          const oErrorData = validator.fn.core.collectErrorData($inputField, 'required-field');

          /* remove standard classes for missing required field values to field and field label */
          $inputField.removeClass(validator.data.globals.INPUT_ERROR_BASE_CLASS + validator.data.globals.REQUIRED_ERROR_SUFFIX);
          oErrorData.label.removeClass(validator.data.globals.LABEL_ERROR_BASE_CLASS + validator.data.globals.REQUIRED_ERROR_SUFFIX);

          /* should be configurable to allow the calling a custom function within the app */
          spartaEventsUtility.pub(validator.EVENTS.REQUIRED_FIELD_SUCCESS_EVENT, oErrorData);
        }
      }
    };

    /* Add AML Validation Rules to the this.fn.validate object */
    this.fn.validate.AML = this.AMLValidationRules;
  }

  /**
   * @property {Object} messages() exposes validation errors list object
   * @description getter for this.messageList object
   * @returns {Object} this.messageList
   */
  get messages() {
    return this.messageList;
  }

  /**
   * @property {Object} data() exposes the Sparta Validation Framework data constant
   * @description getter for the data constant
   * @returns {Object} data
   */
  get data() {
    return data;
  }

  /**
   * @property {Object} EVENTS() exposes the Sparta Validation Framework EVENTS constant
   * @description getter for the EVENTS constant
   * @returns {Object} EVENTS
   */
  get EVENTS() {
    return EVENTS;
  }
}
const spartaValidationFramework = new SpartaValidator();

export { spartaValidationFramework as default };
